import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePortfolio } from './hooks/usePortfolio';
import { useCollectionApi } from './hooks/useCollectionApi';
import { apiGet, apiPut } from './api';
import THEMES, { CHART_COLORS } from './utils/themes';
import LANG, { STATUSES, ACTIONS, STATUS_COLORS, ACTION_COLORS, CLIENT_STATUS_COLORS, FU_STATUSES, FU_ACTIONS, FU_STATUS_COLORS, FU_ACTION_COLORS } from './utils/lang';
import { parsePortfolioExcel, parseAccountsExcel, parseFTDExcel, parseLeadsExcel, parseFollowUpExcel, parseNotActiveExcel, exportToExcel, backupAllData, parseBackupExcel, downloadLeadsTemplate } from './utils/parseExcel';
import { StatusBadge, ActionBadge, TypeBadge, RoleBadge, StatCard, ChartCard, Toast, Dropdown, MultiDropdown, CopyCell, CopyGuard, SortableTable, FiltersBar, UploadButton, Spinner, Modal } from './components/UI';
import LoginPage from './pages/Login';

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend } from 'recharts';

var IH_SIGNATURE = 'data:image/png;base64,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';


function AppInner() {
  // Helper: format any date to local Jordan time display string
  var localFmt = function(dateVal) {
    if (!dateVal) return '';
    var d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0') + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  };
  // Helper: get current local time as ISO-like string (for storage)
  var localNow = function() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + 'T' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0');
  };
  var normAgent = function(v) {
    return String(v || '').trim().toLowerCase();
  };
  var RIVERPRIME_LEAD_SOURCE = 'RiverPrime Leads';
  var normalizeLeadSource = function(v) {
    var s = String(v || '').trim();
    if (!s) return '';
    var lower = s.toLowerCase();
    if (lower.indexOf('riverprime') >= 0 || lower === 'i. alhroub') return RIVERPRIME_LEAD_SOURCE;
    return s;
  };

  var authCtx = useAuth();
  var { userProfile, login, logout, getAllUsers, createUser, updateUser, deleteUser, resetPassword, loading: authLoading } = authCtx;

  var [lang, setLang] = useState('en');
  var [thKey, setThKey] = useState('hybrid');
  var [tab, setTab] = useState('dashboard');
  var [allUsers, setAllUsers] = useState([]);
  var [usersLoaded, setUsersLoaded] = useState(false);
  // ═══ PER-TAB FILTER SYSTEM ═══
  var [_tabFilters, _setTabFilters] = useState({ accounts: { dateFrom: (function() { var n = new Date(); return n.getFullYear() + '-' + String(n.getMonth() + 1).padStart(2, '0') + '-01'; })() } });
  var _tf = _tabFilters[tab] || {};
  var search = _tf.search || '';
  var fAgent = _tf.fAgent || [];
  var fNewAgent = _tf.fNewAgent || [];
  var fStatus = _tf.fStatus || [];
  var fAction = _tf.fAction || [];
  var fAccCountry = _tf.fAccCountry || [];
  var fAccClientStatus = _tf.fAccClientStatus || [];
  var dateFrom = _tf.dateFrom || '';
  var dateTo = _tf.dateTo || '';
  var fuFilterStatus = _tf.fuFilterStatus || [];
  var fuFilterAction = _tf.fuFilterAction || [];
  var _setTF = function(key, val) {
    _setTabFilters(function(prev) {
      var t = tab;
      var updated = {};
      Object.keys(prev).forEach(function(k) { updated[k] = prev[k]; });
      updated[t] = {};
      if (prev[t]) Object.keys(prev[t]).forEach(function(k) { updated[t][k] = prev[t][k]; });
      updated[t][key] = val;
      return updated;
    });
  };
  var setSearch = function(v) { _setTF('search', v); };
  var setFAgent = function(v) { _setTF('fAgent', v); };
  var setFNewAgent = function(v) { _setTF('fNewAgent', v); };
  var setFStatus = function(v) { _setTF('fStatus', v); };
  var setFAction = function(v) { _setTF('fAction', v); };
  var setFAccCountry = function(v) { _setTF('fAccCountry', v); };
  var setFAccClientStatus = function(v) { _setTF('fAccClientStatus', v); };
  var setDateFrom = function(v) { _setTF('dateFrom', v); };
  var setDateTo = function(v) { _setTF('dateTo', v); };
  var setFuFilterStatus = function(v) { _setTF('fuFilterStatus', v); };
  var setFuFilterAction = function(v) { _setTF('fuFilterAction', v); };
  var [toast, setToast] = useState('');
  // ═══ EXCLUDE CLIENTS FEATURE ═══
  var [excludedCUs, setExcludedCUs] = useState([]);
  var [excludedLogins, setExcludedLogins] = useState([]);
  var [showExcludePanel, setShowExcludePanel] = useState(false);
  var [excludeMode, setExcludeMode] = useState('cu'); // 'cu' or 'login'
  var [excludeInput, setExcludeInput] = useState('');
  var [excludeBulk, setExcludeBulk] = useState('');
  var excludeLoadedRef = useRef(false);
  // Load exclusions from backend settings
  useEffect(function() {
    if (!userProfile || excludeLoadedRef.current) return;
    excludeLoadedRef.current = true;
    apiGet('/api/settings/exclusions').then(function(res) {
      var d = res.value;
      if (d) {
        if (d.excludedCUs) setExcludedCUs(d.excludedCUs);
        if (d.excludedLogins) setExcludedLogins(d.excludedLogins);
      }
    }).catch(function() {});
  }, [userProfile]);
  // Save exclusions to backend settings
  var saveExclusions = function(cus, logins) {
    apiPut('/api/settings/exclusions', { value: { excludedCUs: cus, excludedLogins: logins } }).catch(function() {});
  };
  var addExcludeCU = function(val) {
    var v = val.trim().toUpperCase();
    if (!v || excludedCUs.indexOf(v) >= 0) return;
    var next = excludedCUs.concat([v]);
    setExcludedCUs(next);
    saveExclusions(next, excludedLogins);
  };
  var addExcludeLogin = function(val) {
    var v = val.trim();
    if (!v || excludedLogins.indexOf(v) >= 0) return;
    var next = excludedLogins.concat([v]);
    setExcludedLogins(next);
    saveExclusions(excludedCUs, next);
  };
  var removeExcludeCU = function(val) {
    var next = excludedCUs.filter(function(c) { return c !== val; });
    setExcludedCUs(next);
    saveExclusions(next, excludedLogins);
  };
  var removeExcludeLogin = function(val) {
    var next = excludedLogins.filter(function(l) { return l !== val; });
    setExcludedLogins(next);
    saveExclusions(excludedCUs, next);
  };
  var clearAllExclusions = function() {
    setExcludedCUs([]);
    setExcludedLogins([]);
    saveExclusions([], []);
  };
  var handleAddExclude = function() {
    if (!excludeInput.trim()) return;
    if (excludeMode === 'cu') addExcludeCU(excludeInput);
    else addExcludeLogin(excludeInput);
    setExcludeInput('');
  };
  var handleBulkExclude = function() {
    if (!excludeBulk.trim()) return;
    var items = excludeBulk.split(/[,\n\r]+/).map(function(s) { return s.trim(); }).filter(Boolean);
    if (excludeMode === 'cu') {
      var nextCUs = excludedCUs.slice();
      items.forEach(function(v) { v = v.toUpperCase(); if (nextCUs.indexOf(v) < 0) nextCUs.push(v); });
      setExcludedCUs(nextCUs);
      saveExclusions(nextCUs, excludedLogins);
    } else {
      var nextLogins = excludedLogins.slice();
      items.forEach(function(v) { if (nextLogins.indexOf(v) < 0) nextLogins.push(v); });
      setExcludedLogins(nextLogins);
      saveExclusions(excludedCUs, nextLogins);
    }
    setExcludeBulk('');
  };
  var [showAddLead, setShowAddLead] = useState(false);
  var [showAddUser, setShowAddUser] = useState(false);
  var [newLead, setNewLead] = useState({ client: '', phone: '', agent: '', date: localNow().slice(0, 10) });
  var [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'agent', assignedAgents: [] });
  var [editLead, setEditLead] = useState(null);
  var [editingLeadId, setEditingLeadId] = useState(null);
  var [editingLeadData, setEditingLeadData] = useState({});
  var [commentPopup, setCommentPopup] = useState(null);
  var [selectedLeads, setSelectedLeads] = useState({});
  var [editUser, setEditUser] = useState(null);
  var [createdCreds, setCreatedCreds] = useState(null);
  var [newLeadNotif, setNewLeadNotif] = useState(null);
  var [unseenLeadIds, setUnseenLeadIds] = useState([]);
  var [reminderPopup, setReminderPopup] = useState(null);
  var [showNotifDropdown, setShowNotifDropdown] = useState(false);
  var firedRemindersRef = useRef({});
  var prevLeadIdsRef = useRef(null);

  // Fun notification sound (cha-ching!)
  var playNotifSound = useCallback(function() {
    try {
      var ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Cha-ching: two quick bright tones
      [0, 0.15].forEach(function(delay, idx) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = idx === 0 ? 1200 : 1600;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.25);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.25);
      });
    } catch(e) {}
  }, []);

  // Auto-generate simple password
  var genPassword = function() {
    var words = ['Sun', 'Moon', 'Star', 'Blue', 'Red', 'Gold', 'Fast', 'Cool', 'Top', 'Max'];
    var w = words[Math.floor(Math.random() * words.length)];
    var n = Math.floor(Math.random() * 900) + 100;
    return w + n + '!';
  };
  var [uploading, setUploading] = useState(false);
  var [pMonth, setPMonth] = useState([]);
  var [pYear, setPYear] = useState([]);
  var [pCountry, setPCountry] = useState([]);
  var [pFunded, setPFunded] = useState([]);
  var [pClientStatus, setPClientStatus] = useState([]);
  var [pAccType, setPAccType] = useState([]);
  var [pClientType, setPClientType] = useState([]);
  var [pInstrument, setPInstrument] = useState([]);
  var [pMostTraded, setPMostTraded] = useState([]);
  var [pCU, setPCU] = useState([]);
  var [pLogin, setPLogin] = useState([]);
  var [pClientName, setPClientName] = useState([]);
  var [fFtdType, setFFtdType] = useState([]);
  var [ftdMonth, setFtdMonth] = useState([new Date().toLocaleString('en', { month: 'long' })]);
  var [ftdYear, setFtdYear] = useState([String(new Date().getFullYear())]);
  var [ftdViewMode, setFtdViewMode] = useState('ftd');
  var [fuModal, setFuModal] = useState(null);
  var [highlightId, setHighlightId] = useState(null);

  var ll = LANG[lang];
  var th = THEMES[thKey];
  var rtl = lang === 'ar';
  var role = userProfile ? userProfile.role : null;

  // Data hooks
  var dataHooksEnabled = !!userProfile && !authLoading;
  var { portfolioData, portfolioLoading, portfolioLastUpdate, uploadPortfolio } = usePortfolio({ enabled: dataHooksEnabled, initialDelayMs: 0 });
  var accountsHook = useCollectionApi('accounts', { enabled: dataHooksEnabled, initialDelayMs: 200, pollMs: 60000 });
  var ftdHook = useCollectionApi('ftd', { enabled: dataHooksEnabled, initialDelayMs: 450, pollMs: 60000 });
  var leadsHook = useCollectionApi('leads', { enabled: dataHooksEnabled, initialDelayMs: 1200, pollMs: 90000 });
  var followupsHook = useCollectionApi('followups', { enabled: dataHooksEnabled, initialDelayMs: 1700, pollMs: 90000 });
  var notActiveHook = useCollectionApi('notactive', { enabled: dataHooksEnabled, initialDelayMs: 1950, pollMs: 90000 });
  var notifsHook = useCollectionApi('notifications', { enabled: dataHooksEnabled, initialDelayMs: 2200, pollMs: 30000 });
  var [dashboardSummary, setDashboardSummary] = useState(null);
  var [dashboardSummaryLoading, setDashboardSummaryLoading] = useState(false);

  useEffect(function() {
    if (!dataHooksEnabled) {
      setDashboardSummary(null);
      setDashboardSummaryLoading(false);
      return;
    }
    var mounted = true;
    var loadSummary = function() {
      setDashboardSummaryLoading(true);
      apiGet('/api/dashboard/summary')
        .then(function(res) {
          if (!mounted) return;
          setDashboardSummary(res || null);
        })
        .catch(function(err) {
          if (!mounted) return;
          console.error('Dashboard summary load error:', err);
        })
        .finally(function() {
          if (mounted) setDashboardSummaryLoading(false);
        });
    };

    loadSummary();
    var timer = setInterval(loadSummary, 60000);

    return function() {
      mounted = false;
      clearInterval(timer);
    };
  }, [dataHooksEnabled]);

  // Follow-up lookup by login (for 💬 indicator on Portfolio)
  var fuByLogin = useMemo(function() {
    var map = {};
    (followupsHook.data || []).forEach(function(f) {
      var k = String(f.login || '');
      if (!k) return;
      if (!map[k] || (f.updatedAt || '') > (map[k].updatedAt || '')) map[k] = f;
    });
    return map;
  }, [followupsHook.data]);

  var showMsg = function(m) { setToast(m); setTimeout(function() { setToast(''); }, 2500); };
  var resetF = function() { _setTabFilters(function(prev) { var u = {}; Object.keys(prev).forEach(function(k) { u[k] = prev[k]; }); u[tab] = {}; return u; }); setPMonth([]); setPYear([]); setPCountry([]); setPFunded([]); setPClientStatus([]); setPAccType([]); setPClientType([]); setPInstrument([]); setPMostTraded([]); setPCU([]); setPLogin([]); setPClientName([]); };

  // Load users list
  var loadUsers = useCallback(async function() {
    if (usersLoaded) return allUsers;
    try {
      var users = await getAllUsers();
      setAllUsers(users);
      setUsersLoaded(true);
      return users;
    } catch (e) {
      console.error('Load users error:', e);
      return [];
    }
  }, [getAllUsers, usersLoaded, allUsers]);

  // Get visible agent names based on role
  var visibleAgentNames = useMemo(function() {
    if (!userProfile) return [];
    if (role === 'admin') return [];
    if (role === 'manager') {
      var assigned = userProfile.assignedAgents || [];
      var assignedSet = new Set(assigned);
      var canonical = new Set();
      allUsers.forEach(function(u) {
        if (u.role !== 'agent') return;
        var dataName = u.dataAgentName || u.name;
        if (assignedSet.has(u.uid) || assignedSet.has(u.name) || assignedSet.has(dataName)) {
          if (dataName) canonical.add(dataName);
        }
      });
      assigned.forEach(function(v) {
        if (typeof v === 'string' && v.trim()) canonical.add(v.trim());
      });
      return Array.from(canonical);
    }
    // Agent: use dataAgentName if set, otherwise fallback to profile name
    return [userProfile.dataAgentName || userProfile.name];
  }, [userProfile, role, allUsers]);

  // Filter data by visible agents
  var filterByAgent = function(arr, agentKey) {
    if (role === 'admin') return arr; // Admin sees ALL data
    if (!visibleAgentNames.length) return arr;
    return arr.filter(function(x) { return visibleAgentNames.indexOf(x[agentKey || 'agent']) >= 0; });
  };

  // All unique agent names from data (for admin dropdown)
  var allAgentNamesFromData = useMemo(function() {
    if (role !== 'admin') return visibleAgentNames;
    var names = new Set();
    portfolioData.forEach(function(p) { if (p.agent) names.add(p.agent); });
    accountsHook.data.forEach(function(p) { if (p.agent) names.add(p.agent); });
    ftdHook.data.forEach(function(p) { if (p.agent) names.add(p.agent); });
    notActiveHook.data.forEach(function(p) {
      if (p.currentAgent) names.add(p.currentAgent);
      if (p.agent) names.add(p.agent);
      if (p.newAgent) names.add(p.newAgent);
    });
    // Also include agents from Users collection (so new agents with no data appear)
    allUsers.forEach(function(u) {
      if (u.role === 'agent') {
        var dn = u.dataAgentName || u.name;
        if (dn) names.add(dn);
      }
    });
    return Array.from(names).sort();
  }, [role, portfolioData, accountsHook.data, ftdHook.data, notActiveHook.data, visibleAgentNames, allUsers]);

  var myPortRaw = useMemo(function() { return filterByAgent(portfolioData, 'agent'); }, [portfolioData, visibleAgentNames]);
  // Apply exclusions
  var excludedCUSet = useMemo(function() { return new Set(excludedCUs); }, [excludedCUs]);
  var excludedLoginSet = useMemo(function() { return new Set(excludedLogins); }, [excludedLogins]);
  var myPort = useMemo(function() {
    if (!excludedCUSet.size && !excludedLoginSet.size) return myPortRaw;
    return myPortRaw.filter(function(p) {
      if (excludedCUSet.size && excludedCUSet.has((p.cu || '').toUpperCase())) return false;
      if (excludedLoginSet.size && excludedLoginSet.has(String(p.login || ''))) return false;
      return true;
    });
  }, [myPortRaw, excludedCUSet, excludedLoginSet]);
  var allPort = useMemo(function() {
    if (!excludedCUSet.size && !excludedLoginSet.size) return portfolioData;
    return portfolioData.filter(function(p) {
      if (excludedCUSet.size && excludedCUSet.has((p.cu || '').toUpperCase())) return false;
      if (excludedLoginSet.size && excludedLoginSet.has(String(p.login || ''))) return false;
      return true;
    });
  }, [portfolioData, excludedCUSet, excludedLoginSet]);
  var excludedPortCount = myPortRaw.length - myPort.length;
  var excludedCUCount = useMemo(function() {
    if (!excludedCUSet.size && !excludedLoginSet.size) return 0;
    var allCUs = new Set(myPortRaw.map(function(p) { return p.cu; }));
    var filteredCUs = new Set(myPort.map(function(p) { return p.cu; }));
    return allCUs.size - filteredCUs.size;
  }, [myPortRaw, myPort, excludedCUSet, excludedLoginSet]);
  var myAcc = useMemo(function() { return filterByAgent(accountsHook.data, 'agent'); }, [accountsHook.data, visibleAgentNames]);
  // New Accounts view: only show 2026+ registrations
  var myAcc2026 = useMemo(function() {
    return myAcc.filter(function(a) { return (a.registration || a.date || '') >= '2026-01-01'; });
  }, [myAcc]);
  var myFtd = useMemo(function() { return filterByAgent(ftdHook.data, 'agent'); }, [ftdHook.data, visibleAgentNames]);
  var myLeads = useMemo(function() {
    if (role === 'admin') return leadsHook.data;
    if (role === 'manager') {
      // Manager sees their agents' leads + unassigned leads (empty agent)
      return leadsHook.data.filter(function(x) {
        return !x.agent || x.agent.trim() === '' || visibleAgentNames.indexOf(x.agent) >= 0;
      });
    }
    return filterByAgent(leadsHook.data, 'agent');
  }, [leadsHook.data, visibleAgentNames, role]);

  // Normalize phone to dedup key: strip non-digits, take last 9 (minimum 9 digits required)
  var phoneKey = function(phone) {
    var digits = String(phone || '').replace(/\D/g, '');
    if (digits.length >= 9) return digits.slice(-9);
    return '';
  };

  // Deduplicate leads by phone key - keep latest per phone
  var myLeadsDeduped = useMemo(function() {
    var phoneMap = {};
    myLeads.forEach(function(ld) {
      var key = phoneKey(ld.phone);
      if (!key) { phoneMap['no_phone_' + ld._id] = ld; return; }
      if (!phoneMap[key] || (ld.date || '') > (phoneMap[key].date || '')) {
        phoneMap[key] = ld;
      }
    });
    return Object.values(phoneMap);
  }, [myLeads]);

  var accountByLogin = useMemo(function() {
    var map = {};
    accountsHook.data.forEach(function(a) {
      var key = String(a.login || '');
      if (!key) return;
      if (!map[key] || String(a.registration || a.date || '') > String(map[key].registration || map[key].date || '')) {
        map[key] = a;
      }
    });
    return map;
  }, [accountsHook.data]);

  // Startup check ref - declared here so real-time detector can read it
  var startupCheckDoneRef = useRef(false);

  // Detect new leads for agent notification (only AFTER startup check completes)
  useEffect(function() {
    if (role !== 'agent' || !userProfile) return;
    // Skip entirely until startup check is done - prevents duplicate notifications during initial sync
    if (!startupCheckDoneRef.current) return;
    var currentIds = myLeads.map(function(l) { return l._id; });
    if (prevLeadIdsRef.current === null) {
      // First run after startup - just store IDs, no notification
      prevLeadIdsRef.current = currentIds;
      return;
    }
    var prevSet = new Set(prevLeadIdsRef.current);
    var brandNew = myLeads.filter(function(l) { return !prevSet.has(l._id); });
    if (brandNew.length > 0) {
      playNotifSound();
      setNewLeadNotif(brandNew[0]);
      setUnseenLeadIds(function(prev) { return prev.concat(brandNew.map(function(l) { return l._id; })); });
      // Check existing notifications to avoid duplicates
      var existingLeadIds = new Set((notifsHook.data || []).filter(function(n) { return n.type === 'new_lead'; }).map(function(n) { return n.leadId; }));
      brandNew.forEach(function(l) {
        if (!existingLeadIds.has(l._id)) {
          notifsHook.addItem({ agent: userProfile.dataAgentName || userProfile.name, type: 'new_lead', title: 'New Lead: ' + (l.client || 'Unknown'), message: (l.phone || '') + (l.date ? ' • ' + l.date : ''), leadId: l._id, createdAt: localNow(), dismissed: false });
        }
      });
    }
    prevLeadIdsRef.current = currentIds;
  }, [myLeads, role, userProfile, playNotifSound]);

  // Startup check: create notifications for leads/reminders that were missed while offline (agents only)
  useEffect(function() {
    if (!userProfile || role !== 'agent' || startupCheckDoneRef.current) return;
    if (notifsHook.loading || leadsHook.loading || followupsHook.loading) return;
    startupCheckDoneRef.current = true;

    var agentName = userProfile.dataAgentName || userProfile.name;
    var existingNotifs = notifsHook.data || [];

    // 1. Check for leads without notifications (new leads assigned while offline) - only last 7 days
    if (role === 'agent') {
      var sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      var cutoffDate = sevenDaysAgo.getFullYear() + '-' + String(sevenDaysAgo.getMonth() + 1).padStart(2, '0') + '-' + String(sevenDaysAgo.getDate()).padStart(2, '0');
      var notifiedLeadIds = new Set(existingNotifs.filter(function(n) { return n.type === 'new_lead' && n.agent === agentName; }).map(function(n) { return n.leadId; }));
      myLeads.forEach(function(ld) {
        if (!notifiedLeadIds.has(ld._id) && (ld.date || '') >= cutoffDate) {
          notifsHook.addItem({ agent: agentName, type: 'new_lead', title: 'New Lead: ' + (ld.client || 'Unknown'), message: (ld.phone || '') + (ld.date ? ' • ' + ld.date : ''), leadId: ld._id, createdAt: localNow(), dismissed: false });
        }
      });
    }

    // 2. Check for past-due lead reminders (use local time)
    var now = new Date();
    var nowStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0') + 'T' + String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
    var notifiedReminderKeys = new Set(existingNotifs.filter(function(n) { return n.type === 'reminder'; }).map(function(n) { return (n.leadId || '') + '_' + (n.fuId || ''); }));

    myLeads.forEach(function(ld) {
      if (ld.reminderDate && ld.reminderTime) {
        var reminderStr = ld.reminderDate + 'T' + ld.reminderTime;
        if (reminderStr <= nowStr && !notifiedReminderKeys.has(ld._id + '_')) {
          firedRemindersRef.current['lead_' + ld._id] = true;
          notifsHook.addItem({ agent: ld.agent || agentName, type: 'reminder', title: 'Reminder: ' + (ld.client || 'Client'), message: ld.comments ? ld.comments.slice(0, 80) : '', leadId: ld._id, fuId: '', createdAt: localNow(), dismissed: false });
        }
      }
    });

    // 3. Check for past-due follow-up reminders (only for this agent, deduplicate by client name)
    var fuNotifiedClients = new Set();
    (followupsHook.data || []).filter(function(fu) { return fu.agent === agentName; }).forEach(function(fu) {
      if (fu.reminderDate && fu.reminderTime) {
        var reminderStr = fu.reminderDate + 'T' + fu.reminderTime;
        var clientKey = (fu.name || fu.cu || '') + '_' + fu.reminderDate + '_' + fu.reminderTime;
        if (reminderStr <= nowStr && !notifiedReminderKeys.has('_' + fu._id)) {
          firedRemindersRef.current['fu_' + fu._id] = true;
          if (!fuNotifiedClients.has(clientKey)) {
            fuNotifiedClients.add(clientKey);
            notifsHook.addItem({ agent: fu.agent || agentName, type: 'reminder', title: 'Reminder: ' + (fu.name || fu.cu || 'Client'), message: fu.comment ? fu.comment.slice(0, 80) : '', leadId: '', fuId: fu._id, createdAt: localNow(), dismissed: false });
          }
        }
      }
    });
  }, [userProfile, role, myLeads, notifsHook.loading, leadsHook.loading, followupsHook.loading]);

  // Independent popup watcher - shows popup for undismissed new_lead notifications
  var newLeadPopupShownRef = useRef(false);
  useEffect(function() {
    if (role !== 'agent' || !userProfile || newLeadPopupShownRef.current) return;
    var notifs = notifsHook.data || [];
    if (notifs.length === 0) return;
    var undismissed = notifs.filter(function(n) { return n.type === 'new_lead' && !n.dismissed; });
    if (undismissed.length === 0) return;
    var latest = undismissed.sort(function(a, b) { return (b.createdAt || '').localeCompare(a.createdAt || ''); })[0];
    var matchLead = myLeads.find(function(l) { return l._id === latest.leadId; });
    if (matchLead) {
      newLeadPopupShownRef.current = true;
      playNotifSound();
      setNewLeadNotif(matchLead);
      setUnseenLeadIds(undismissed.map(function(n) { return n.leadId; }));
    }
  }, [notifsHook.data, myLeads, role, userProfile, playNotifSound]);

  // Reminder checker - check every 15 seconds for due reminders (agents only)
  useEffect(function() {
    if (!userProfile || role !== 'agent') return;
    var checkReminders = function() {
      // Wait until startup check has completed to avoid duplicate notifications
      if (!startupCheckDoneRef.current) return;
      var now = new Date();
      var nowStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0') + 'T' + String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
      // Check lead reminders
      myLeads.forEach(function(ld) {
        if (ld.reminderDate && ld.reminderTime && !firedRemindersRef.current['lead_' + ld._id]) {
          var reminderStr = ld.reminderDate + 'T' + ld.reminderTime;
          if (reminderStr <= nowStr) {
            firedRemindersRef.current['lead_' + ld._id] = true;
            playNotifSound();
            setReminderPopup({ client: ld.client, phone: ld.phone, comments: ld.comments, id: ld._id, type: 'lead' });
            notifsHook.addItem({ agent: ld.agent || (userProfile ? (userProfile.dataAgentName || userProfile.name) : ''), type: 'reminder', title: 'Reminder: ' + (ld.client || 'Client'), message: ld.comments ? ld.comments.slice(0, 80) : '', leadId: ld._id, fuId: '', createdAt: localNow(), dismissed: false });
          }
        }
      });
      // Check follow-up reminders (only for this agent, one popup per batch)
      var agentName = userProfile ? (userProfile.dataAgentName || userProfile.name) : '';
      var fuPopupFired = false;
      (followupsHook.data || []).filter(function(fu) { return fu.agent === agentName; }).forEach(function(fu) {
        if (fu.reminderDate && fu.reminderTime && !firedRemindersRef.current['fu_' + fu._id]) {
          var reminderStr = fu.reminderDate + 'T' + fu.reminderTime;
          if (reminderStr <= nowStr) {
            firedRemindersRef.current['fu_' + fu._id] = true;
            if (!fuPopupFired) {
              fuPopupFired = true;
              playNotifSound();
              setReminderPopup({ client: fu.name || fu.cu, phone: '', comments: fu.comment, id: fu._id, type: 'followup' });
              notifsHook.addItem({ agent: agentName, type: 'reminder', title: 'Reminder: ' + (fu.name || fu.cu || 'Client'), message: fu.comment ? fu.comment.slice(0, 80) : '', leadId: '', fuId: fu._id, createdAt: localNow(), dismissed: false });
            }
          }
        }
      });
    };
    checkReminders();
    var interval = setInterval(checkReminders, 15000);
    return function() { clearInterval(interval); };
  }, [myLeads, followupsHook.data, userProfile, playNotifSound]);

  // Filter helpers
  var filterDate = function(arr, dk) {
    return arr.filter(function(x) {
      if (dateFrom && x[dk] < dateFrom) return false;
      if (dateTo && x[dk] > dateTo) return false;
      return true;
    });
  };
  var filterSearch = function(arr) {
    if (!search) return arr;
    var s = search.toLowerCase();
    return arr.filter(function(x) {
      return Object.values(x).some(function(v) { return v && String(v).toLowerCase().indexOf(s) >= 0; });
    });
  };

  var parseDateOnly = function(v) {
    var raw = String(v || '').trim();
    if (!raw || raw === 'NULL') return '';
    var d = new Date(raw);
    if (isNaN(d.getTime())) {
      var m = raw.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
      if (!m) return '';
      return m[1] + '-' + m[2] + '-' + m[3];
    }
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };

  var daysSinceDate = function(v) {
    var dStr = parseDateOnly(v);
    if (!dStr) return null;
    var d = new Date(dStr + 'T00:00:00');
    if (isNaN(d.getTime())) return null;
    var now = new Date();
    var tNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    var tPast = d.getTime();
    return Math.floor((tNow - tPast) / 86400000);
  };

  // Load users on first render after login
  React.useEffect(function() {
    if (userProfile && !usersLoaded) loadUsers();
  }, [userProfile, usersLoaded, loadUsers]);

  // Get latest month in data for chart filtering — MUST be before all chart useMemos
  var latestMonthKey = useMemo(function() {
    var maxMK = 0;
    myPort.forEach(function(p) { var mk = Number(p.monthKey) || 0; if (mk > maxMK) maxMK = mk; });
    return maxMK;
  }, [myPort]);

  var mkToLabel = function(mk) {
    if (!mk) return '';
    var y = String(mk).slice(0, 4);
    var m = parseInt(String(mk).slice(4, 6));
    var names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return names[m] + ' ' + y;
  };

  var latestMonthLabel = useMemo(function() { return mkToLabel(latestMonthKey); }, [latestMonthKey]);

  // Independent latest months per data source
  var latestFtdMonth = useMemo(function() {
    var maxYM = '';
    myFtd.forEach(function(f) { var ym = (f.ftdDate || '').slice(0, 7); if (ym > maxYM) maxYM = ym; });
    return maxYM;
  }, [myFtd]);

  var latestFtdLabel = useMemo(function() {
    if (!latestFtdMonth) return '';
    var mk = latestFtdMonth.replace('-', '');
    return mkToLabel(mk);
  }, [latestFtdMonth]);

  var latestAccMonth = useMemo(function() {
    var maxYM = '';
    myAcc2026.forEach(function(a) { var ym = (a.registration || a.date || '').slice(0, 7); if (ym > maxYM) maxYM = ym; });
    return maxYM;
  }, [myAcc2026]);

  var latestAccLabel = useMemo(function() {
    if (!latestAccMonth) return '';
    var mk = latestAccMonth.replace('-', '');
    return mkToLabel(mk);
  }, [latestAccMonth]);

  // Current month portfolio data (for charts)
  var myPortCurrentMonth = useMemo(function() {
    if (!latestMonthKey) return myPort;
    return myPort.filter(function(p) { return Number(p.monthKey) === latestMonthKey; });
  }, [myPort, latestMonthKey]);

  // Retention data - reactivated logins from current/latest month
  var myRetention = useMemo(function() {
    var newClientCUs = new Set(myAcc.filter(function(a) { return a.clientStatus === 'New Client'; }).map(function(a) { return a.id; }));
    return myPortCurrentMonth.filter(function(p) {
      return p.isReactivated && p.isReactivated.toLowerCase() === 'reactivated' && !newClientCUs.has(p.cu);
    });
  }, [myPortCurrentMonth, myAcc]);

  // Chart data
  var tipStyle = { background: th.card, border: '1px solid ' + th.border, borderRadius: 8, fontSize: 11, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', color: th.text };

  var ftdChart = useMemo(function() {
    var ym = latestFtdMonth || '';
    // Build login→agent lookup from portfolio for FTD records missing agent
    var loginAgent = {};
    myPort.forEach(function(p) { if (p.login && p.agent) loginAgent[String(p.login)] = p.agent; });
    var filtered = ym ? myFtd.filter(function(x) { return (x.ftdDate || '').slice(0, 7) === ym; }) : myFtd;
    return allAgentNamesFromData.map(function(a, i) {
      return { name: a.split(' ')[0], val: filtered.filter(function(x) { return (x.agent || loginAgent[String(x.login || '')] || '') === a; }).reduce(function(s, x) { return s + (Number(x.ftdAmount) || 0); }, 0), fill: CHART_COLORS[i % 10] };
    }).filter(function(x) { return x.val > 0; });
  }, [allAgentNamesFromData, myFtd, myPort, latestFtdMonth]);

  var nonFundedChart = useMemo(function() {
    var ftdLogins = new Set();
    myFtd.forEach(function(f) { if (String(f.login || '')) ftdLogins.add(String(f.login)); });
    // Use portfolio data (myPort) to find all logins not in FTD - matches FTD tab logic
    var nfGroups = {};
    myPort.forEach(function(p) {
      var key = String(p.login || '');
      if (!key || ftdLogins.has(key)) return;
      if (!nfGroups[key] || (Number(p.monthKey) || 0) > (Number(nfGroups[key].monthKey) || 0)) nfGroups[key] = p;
    });
    var agentCounts = {};
    Object.values(nfGroups).forEach(function(p) {
      var agent = p.agent || 'Unknown';
      if (!agentCounts[agent]) agentCounts[agent] = 0;
      agentCounts[agent]++;
    });
    return allAgentNamesFromData.map(function(a, i) {
      return { name: a.split(' ')[0], val: agentCounts[a] || 0, fill: CHART_COLORS[i % 10] };
    }).filter(function(x) { return x.val > 0; });
  }, [allAgentNamesFromData, myPort, myFtd]);

  var totalNonFunded = useMemo(function() {
    var ftdLogins = new Set();
    myFtd.forEach(function(f) { if (String(f.login || '')) ftdLogins.add(String(f.login)); });
    // Use portfolio data (myPort) to find all logins not in FTD - matches FTD tab logic
    var nfLogins = new Set();
    myPort.forEach(function(p) {
      var key = String(p.login || '');
      if (key && !ftdLogins.has(key)) nfLogins.add(key);
    });
    return nfLogins.size;
  }, [myPort, myFtd]);

  var leadsChart = useMemo(function() {
    return allAgentNamesFromData.map(function(a, i) {
      return { name: a.split(' ')[0], val: myLeadsDeduped.filter(function(x) { return x.agent === a; }).length, fill: CHART_COLORS[i % 10] };
    }).filter(function(x) { return x.val > 0; });
  }, [allAgentNamesFromData, myLeadsDeduped]);

  var portByType = useMemo(function() {
    var m = {};
    myPort.forEach(function(p) { m[p.accountType] = (m[p.accountType] || 0) + 1; });
    return Object.keys(m).map(function(k, i) { return { name: k, value: m[k], fill: CHART_COLORS[i % 10] }; });
  }, [myPort]);

  // Retention chart for dashboard
  var retentionChart = useMemo(function() {
    return allAgentNamesFromData.map(function(a, i) {
      return { name: a.split(' ')[0], val: myRetention.filter(function(x) { return x.agent === a; }).length, fill: CHART_COLORS[i % 10] };
    }).filter(function(x) { return x.val > 0; });
  }, [allAgentNamesFromData, myRetention]);

  var accChart = useMemo(function() {
    var ym = latestAccMonth || '';
    var filtered = ym ? myAcc2026.filter(function(a) { return (a.registration || a.date || '').slice(0, 7) === ym; }) : myAcc2026;
    var agentLogins = {};
    var agentClients = {};
    filtered.forEach(function(a) {
      var agent = a.agent || 'Unknown';
      agentLogins[agent] = (agentLogins[agent] || 0) + 1;
      if (a.clientStatus === 'New Client') {
        if (!agentClients[agent]) agentClients[agent] = new Set();
        if (a.id) agentClients[agent].add(a.id);
      }
    });
    return allAgentNamesFromData.map(function(a, i) {
      return { name: a.split(' ')[0], logins: agentLogins[a] || 0, clients: agentClients[a] ? agentClients[a].size : 0, fill: CHART_COLORS[i % 10] };
    }).filter(function(x) { return x.logins > 0 || x.clients > 0; });
  }, [allAgentNamesFromData, myAcc2026, latestAccMonth]);

  var newLoginsCount = useMemo(function() {
    var ym = latestAccMonth || '';
    if (!ym) return 0;
    return myAcc2026.filter(function(a) { return (a.registration || a.date || '').slice(0, 7) === ym; }).length;
  }, [myAcc2026, latestAccMonth]);

  var newClientsCount = useMemo(function() {
    var ym = latestAccMonth || '';
    if (!ym) return 0;
    var filtered = myAcc2026.filter(function(a) { return (a.registration || a.date || '').slice(0, 7) === ym && a.clientStatus === 'New Client'; });
    return new Set(filtered.map(function(a) { return a.id; })).size;
  }, [myAcc2026, latestAccMonth]);

  var depVsWd = useMemo(function() {
    return allAgentNamesFromData.map(function(a) {
      var rows = myPortCurrentMonth.filter(function(x) { return x.agent === a; });
      return {
        name: a.split(' ')[0],
        dep: rows.reduce(function(s, x) { return s + (Number(x.deposit) || 0); }, 0),
        wd: Math.abs(rows.reduce(function(s, x) { return s + (Number(x.withdraw) || 0); }, 0)),
      };
    }).filter(function(x) { return x.dep > 0 || x.wd > 0; });
  }, [allAgentNamesFromData, myPortCurrentMonth]);

  var volChart = useMemo(function() {
    return allAgentNamesFromData.map(function(a, i) {
      var rows = myPortCurrentMonth.filter(function(x) { return x.agent === a; });
      return { name: a.split(' ')[0], val: rows.reduce(function(s, x) { return s + (Number(x.volume) || 0); }, 0), fill: CHART_COLORS[i % 10] };
    }).filter(function(x) { return x.val > 0; });
  }, [allAgentNamesFromData, myPortCurrentMonth]);

  // ═══════ PORTFOLIO AGGREGATION ═══════
  var MONTH_ORDER = { January: 1, February: 2, March: 3, April: 4, May: 5, June: 6, July: 7, August: 8, September: 9, October: 10, November: 11, December: 12 };

  // Portfolio filter options (unique values from role-filtered data)
  var portFilterOpts = useMemo(function() {
    var months = new Set(), years = new Set(), countries = new Set();
    var statuses = new Set(), accTypes = new Set(), clientTypes = new Set(), instruments = new Set(), mostTraded = new Set();
    myPort.forEach(function(p) {
      if (p.tradingMonth) months.add(p.tradingMonth);
      if (p.tradingYear) years.add(String(p.tradingYear));
      if (p.country && p.country !== 'NULL' && p.country !== 'Please select') countries.add(p.country);
      if (p.clientStatus) statuses.add(p.clientStatus);
      if (p.accountType && p.accountType !== 'NULL') accTypes.add(p.accountType);
      if (p.clientType && p.clientType !== 'NULL') clientTypes.add(p.clientType);
      if (p.mostInstrument && p.mostInstrument !== 'NULL') instruments.add(p.mostInstrument);
      if (p.mostTradedInstruments && p.mostTradedInstruments !== 'NULL') mostTraded.add(p.mostTradedInstruments);
    });
    return {
      months: Array.from(months).sort(function(a, b) { return (MONTH_ORDER[a] || 0) - (MONTH_ORDER[b] || 0); }),
      years: Array.from(years).sort(),
      countries: Array.from(countries).sort(),
      statuses: Array.from(statuses).sort(),
      accTypes: Array.from(accTypes).sort(),
      clientTypes: Array.from(clientTypes).sort(),
      instruments: Array.from(instruments).sort(),
      mostTraded: Array.from(mostTraded).sort(),
    };
  }, [myPort]);

  // Build login lookup maps for CU/Login/ClientName filters
  var portLoginLookup = useMemo(function() {
    var nameToLogins = {}; var cuToLogins = {};
    myPort.forEach(function(p) {
      var l = String(p.login || '');
      if (!l) return;
      var n = (p.name && p.name !== 'NULL') ? p.name.trim().replace(/\s+/g, ' ') : '';
      var c = p.cu || '';
      if (n) {
        if (!nameToLogins[n]) nameToLogins[n] = new Set();
        nameToLogins[n].add(l);
      }
      if (c) {
        if (!cuToLogins[c]) cuToLogins[c] = new Set();
        cuToLogins[c].add(l);
      }
    });
    return { nameToLogins: nameToLogins, cuToLogins: cuToLogins };
  }, [myPort]);

  var portMatchedLogins = useMemo(function() {
    if (!pCU.length && !pLogin.length && !pClientName.length) return null;
    var sets = [];
    if (pLogin.length) sets.push(new Set(pLogin));
    if (pCU.length) {
      var cuSet = new Set();
      pCU.forEach(function(c) { var s = portLoginLookup.cuToLogins[c]; if (s) s.forEach(function(v) { cuSet.add(v); }); });
      if (cuSet.size) sets.push(cuSet);
    }
    if (pClientName.length) {
      var nameSet = new Set();
      pClientName.forEach(function(n) { var s = portLoginLookup.nameToLogins[n]; if (s) s.forEach(function(v) { nameSet.add(v); }); });
      if (nameSet.size) sets.push(nameSet);
    }
    if (sets.length === 0) return new Set();
    var result = new Set(sets[0]);
    for (var i = 1; i < sets.length; i++) {
      var next = sets[i];
      result.forEach(function(v) { if (!next.has(v)) result.delete(v); });
    }
    return result;
  }, [pCU, pLogin, pClientName, portLoginLookup]);

  // Global latest record per login (for T-fields, always from most recent month regardless of filters)
  var globalLatestByLogin = useMemo(function() {
    var groups = {};
    myPort.forEach(function(p) {
      var key = String(p.login || '');
      if (!key) return;
      if (!groups[key] || (Number(p.monthKey) || 0) > (Number(groups[key].monthKey) || 0)) groups[key] = p;
    });
    return groups;
  }, [myPort]);
  var globalLatestByLoginAll = useMemo(function() {
    var groups = {};
    allPort.forEach(function(p) {
      var key = String(p.login || '');
      if (!key) return;
      if (!groups[key] || (Number(p.monthKey) || 0) > (Number(groups[key].monthKey) || 0)) groups[key] = p;
    });
    return groups;
  }, [allPort]);

  // Filtered + aggregated portfolio data (grouped by Login)
  var portAggregated = useMemo(function() {
    var filtered = myPort.filter(function(p) {
      if (pMonth.length && pMonth.indexOf(p.tradingMonth) < 0) return false;
      if (pYear.length && pYear.indexOf(String(p.tradingYear)) < 0) return false;
      if (pCountry.length && pCountry.indexOf(p.country) < 0) return false;
      if (pClientStatus.length && pClientStatus.indexOf(p.clientStatus) < 0) return false;
      if (pAccType.length && pAccType.indexOf(p.accountType) < 0) return false;
      if (pClientType.length && pClientType.indexOf(p.clientType) < 0) return false;
      if (pInstrument.length && pInstrument.indexOf(p.mostInstrument) < 0) return false;
      if (pMostTraded.length && pMostTraded.indexOf(p.mostTradedInstruments) < 0) return false;
      if (fAgent.length && fAgent.indexOf(p.agent) < 0) return false;
      if (portMatchedLogins !== null && !portMatchedLogins.has(String(p.login || ''))) return false;
      return true;
    });

    // Step 2: Group by Login
    var groups = {};
    filtered.forEach(function(p) {
      var key = p.login;
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });

    // Step 3: Aggregate each group
    var result = Object.keys(groups).map(function(loginKey) {
      var rows = groups[loginKey];
      rows.sort(function(a, b) { return (Number(a.monthKey) || 0) - (Number(b.monthKey) || 0); });
      var latest = rows[rows.length - 1];
      // Get GLOBAL latest for T-fields (not affected by month/year filter)
      var gLatest = globalLatestByLogin[String(loginKey)] || latest;

      // Sum fields
      var sumDep = 0, sumWd = 0, sumVol = 0, sumProf = 0, sumCred = 0, sumDeals = 0, sumTrades = 0;
      var dates = { lastAccess: '', registration: '9999', crmOpen: '9999' };
      rows.forEach(function(r) {
        sumDep += Number(r.deposit) || 0;
        sumWd += Number(r.withdraw) || 0;
        sumVol += Number(r.volume) || 0;
        sumProf += Number(r.profit) || 0;
        sumCred += Number(r.credit) || 0;
        sumDeals += Number(r.totalDeals) || 0;
        sumTrades += Number(r.totalTrades) || 0;
        if (r.lastAccess && r.lastAccess !== 'NULL' && r.lastAccess > dates.lastAccess) dates.lastAccess = r.lastAccess;
        if (r.registration && r.registration !== 'NULL' && r.registration < dates.registration) dates.registration = r.registration;
        if (r.crmOpenDate && r.crmOpenDate !== 'NULL' && r.crmOpenDate < dates.crmOpen) dates.crmOpen = r.crmOpenDate;
      });

      return {
        _id: loginKey,
        monthKey: latest.monthKey || latest.MonthKey || '',
        cu: gLatest.cu || latest.cu,
        login: latest.login,
        name: (gLatest.name && gLatest.name !== 'NULL' ? gLatest.name.trim().replace(/\s+/g, ' ') : (latest.name && latest.name !== 'NULL' ? latest.name.trim().replace(/\s+/g, ' ') : '')),
        phone: (accountByLogin[String(loginKey)] && accountByLogin[String(loginKey)].phone) || '',
        agent: gLatest.agent || latest.agent,
        tDeposit: Number(gLatest.tDeposit) || 0,
        tWithdraw: Number(gLatest.tWithdraw) || 0,
        tVolume: Number(gLatest.tVolume) || 0,
        tProfit: Number(gLatest.tProfit) || 0,
        tCredit: Number(gLatest.tCredit) || 0,
        equity: Number(latest.equity) || 0,
        floatingPL: Number(latest.floatingPL) || 0,
        equityPrevMonth: Number(latest.equityPrevMonth) || 0,
        firstDeposit: Number(gLatest.firstDeposit) || 0,
        deposit: sumDep,
        withdraw: sumWd,
        volume: sumVol,
        profit: sumProf,
        credit: sumCred,
        totalDeals: sumDeals,
        totalTrades: sumTrades,
        lastAccess: dates.lastAccess !== '' ? dates.lastAccess : '',
        registration: dates.registration !== '9999' ? dates.registration : '',
        crmOpenDate: dates.crmOpen !== '9999' ? dates.crmOpen : '',
        clientStatus: latest.clientStatus,
        accountType: latest.accountType,
        clientType: latest.clientType,
        country: latest.country,
        mostInstrument: latest.mostInstrument || '',
        mostTradedInstruments: latest.mostTradedInstruments || '',
        previousClientStatus: latest.previousClientStatus || '',
        isReactivated: latest.isReactivated || '',
        reactivationDate: latest.reactivationDate || '',
      };
    });

    return result;
  }, [myPort, pMonth, pYear, pCountry, pClientStatus, pAccType, pClientType, pInstrument, pMostTraded, fAgent, portMatchedLogins, globalLatestByLogin, accountByLogin]);

  // Separate aggregation for Not Active tab (no portfolio tab filters applied)
  // This ensures the Not Active page shows all records with clientStatus='Not Active'
  // regardless of what filters are selected on the Portfolio tab
  var portAggregatedNotActive = useMemo(function() {
    // Build set of CU accounts that have at least one Active login
    // If a CU has any active account, exclude all their logins from Not Active
    var activeCUs = new Set();
    allPort.forEach(function(p) {
      if (p.clientStatus === 'Active' && p.cu) activeCUs.add(String(p.cu));
    });

    var filtered = allPort.filter(function(p) {
      // Only filter by actual Not Active status, ignore portfolio tab filters
      // Exclude logins whose CU account has at least one active login
      return p.clientStatus === 'Not Active' && !activeCUs.has(String(p.cu));
    });

    // Step 2: Group by Login
    var groups = {};
    filtered.forEach(function(p) {
      var key = p.login;
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });

    // Step 3: Aggregate each group (same logic as portAggregated)
    var result = Object.keys(groups).map(function(loginKey) {
      var rows = groups[loginKey];
      rows.sort(function(a, b) { return (Number(a.monthKey) || 0) - (Number(b.monthKey) || 0); });
      var latest = rows[rows.length - 1];
      var gLatest = globalLatestByLoginAll[String(loginKey)] || latest;

      var sumDep = 0, sumWd = 0, sumVol = 0, sumProf = 0, sumCred = 0, sumDeals = 0, sumTrades = 0;
      var dates = { lastAccess: '', registration: '9999', crmOpen: '9999' };
      rows.forEach(function(r) {
        sumDep += Number(r.deposit) || 0;
        sumWd += Number(r.withdraw) || 0;
        sumVol += Number(r.volume) || 0;
        sumProf += Number(r.profit) || 0;
        sumCred += Number(r.credit) || 0;
        sumDeals += Number(r.totalDeals) || 0;
        sumTrades += Number(r.totalTrades) || 0;
        if (r.lastAccess && r.lastAccess !== 'NULL' && r.lastAccess > dates.lastAccess) dates.lastAccess = r.lastAccess;
        if (r.registration && r.registration !== 'NULL' && r.registration < dates.registration) dates.registration = r.registration;
        if (r.crmOpenDate && r.crmOpenDate !== 'NULL' && r.crmOpenDate < dates.crmOpen) dates.crmOpen = r.crmOpenDate;
      });

      return {
        _id: loginKey,
        cu: gLatest.cu || latest.cu,
        login: latest.login,
        name: (gLatest.name && gLatest.name !== 'NULL' ? gLatest.name.trim().replace(/\s+/g, ' ') : (latest.name && latest.name !== 'NULL' ? latest.name.trim().replace(/\s+/g, ' ') : '')),
        phone: (accountByLogin[String(loginKey)] && accountByLogin[String(loginKey)].phone) || '',
        agent: gLatest.agent || latest.agent,
        tDeposit: Number(gLatest.tDeposit) || 0,
        tWithdraw: Number(gLatest.tWithdraw) || 0,
        tVolume: Number(gLatest.tVolume) || 0,
        tProfit: Number(gLatest.tProfit) || 0,
        tCredit: Number(gLatest.tCredit) || 0,
        equity: Number(latest.equity) || 0,
        floatingPL: Number(latest.floatingPL) || 0,
        equityPrevMonth: Number(latest.equityPrevMonth) || 0,
        firstDeposit: Number(gLatest.firstDeposit) || 0,
        deposit: sumDep,
        withdraw: sumWd,
        volume: sumVol,
        profit: sumProf,
        credit: sumCred,
        totalDeals: sumDeals,
        totalTrades: sumTrades,
        lastAccess: dates.lastAccess !== '' ? dates.lastAccess : '',
        registration: dates.registration !== '9999' ? dates.registration : '',
        crmOpenDate: dates.crmOpen !== '9999' ? dates.crmOpen : '',
        clientStatus: latest.clientStatus,
        accountType: latest.accountType,
        clientType: latest.clientType,
        country: latest.country,
        mostInstrument: latest.mostInstrument || '',
        mostTradedInstruments: latest.mostTradedInstruments || '',
        previousClientStatus: latest.previousClientStatus || '',
        isReactivated: latest.isReactivated || '',
        reactivationDate: latest.reactivationDate || '',
      };
    });

    return result;
  }, [allPort, globalLatestByLoginAll, accountByLogin]);

  // tTotals computed from pFiltered in render section (each row already has gLatest T-values)

  // Portfolio stat totals (from aggregated data)
  var portStats = useMemo(function() {
    var d = portAggregated;
    var lastAcc = '', lastReg = '', lastCRM = '';
    d.forEach(function(r) {
      if (r.lastAccess && r.lastAccess > lastAcc) lastAcc = r.lastAccess;
      if (r.registration && r.registration > lastReg) lastReg = r.registration;
      if (r.crmOpenDate && r.crmOpenDate > lastCRM) lastCRM = r.crmOpenDate;
    });
    return {
      clients: new Set(d.map(function(x) { return x.cu; })).size,
      logins: d.length,
      tDeposit: d.reduce(function(s, x) { return s + x.tDeposit; }, 0),
      tWithdraw: d.reduce(function(s, x) { return s + x.tWithdraw; }, 0),
      tVolume: d.reduce(function(s, x) { return s + x.tVolume; }, 0),
      tProfit: d.reduce(function(s, x) { return s + x.tProfit; }, 0),
      tCredit: d.reduce(function(s, x) { return s + x.tCredit; }, 0),
      deposit: d.reduce(function(s, x) { return s + x.deposit; }, 0),
      withdraw: d.reduce(function(s, x) { return s + x.withdraw; }, 0),
      volume: d.reduce(function(s, x) { return s + x.volume; }, 0),
      profit: d.reduce(function(s, x) { return s + x.profit; }, 0),
      credit: d.reduce(function(s, x) { return s + x.credit; }, 0),
      equity: d.reduce(function(s, x) { return s + x.equity; }, 0),
      floatingPL: d.reduce(function(s, x) { return s + x.floatingPL; }, 0),
      equityPrevMonth: d.reduce(function(s, x) { return s + x.equityPrevMonth; }, 0),
      totalDeals: d.reduce(function(s, x) { return s + x.totalDeals; }, 0),
      totalTrades: d.reduce(function(s, x) { return s + x.totalTrades; }, 0),
      lastAccess: lastAcc,
      lastRegistration: lastReg,
      lastCRM: lastCRM,
    };
  }, [portAggregated]);

  // Upload handlers
  var handleUploadPortfolio = async function(file) {
    setUploading(true);
    showMsg(ll.uploading + ' - parsing file...');
    try {
      var data = await parsePortfolioExcel(file);
      showMsg(ll.uploading + ' - ' + data.length + ' rows parsed, uploading to database...');
      var ok = await uploadPortfolio(data);
      showMsg(ok ? ll.uploadSuccess + ' (' + data.length + ' ' + ll.rows + ')' : ll.uploadError + ' - check console (F12) for details');
    } catch (e) {
      showMsg(ll.uploadError + ': ' + e.message);
      console.error('Upload failed:', e);
    }
    setUploading(false);
  };

  var handleUploadAccounts = async function(file) {
    setUploading(true);
    showMsg(ll.uploading);
    try {
      var data = await parseAccountsExcel(file);
      await accountsHook.replaceAll(data);
      showMsg(ll.uploadSuccess + ' (' + data.length + ' ' + ll.rows + ')');
    } catch (e) { showMsg(ll.uploadError); console.error(e); }
    setUploading(false);
  };

  var handleUploadFTD = async function(file) {
    setUploading(true);
    showMsg(ll.uploading);
    try {
      var data = await parseFTDExcel(file);
      // Enrich with CU + Agent + Name from portfolio and accounts (match by Login)
      var portByLogin = {};
      portfolioData.forEach(function(p) {
        var key = String(p.login || '');
        if (!key) return;
        var mk = Number(p.monthKey) || 0;
        if (!portByLogin[key] || mk > (Number(portByLogin[key].monthKey) || 0)) portByLogin[key] = p;
      });
      var accByLogin = {};
      accountsHook.data.forEach(function(a) {
        var key = String(a.login || '');
        if (key) accByLogin[key] = a;
      });
      data.forEach(function(r) {
        var loginKey = String(r.login || '');
        var accMatch = accByLogin[loginKey];
        var portMatch = portByLogin[loginKey];
        if (!r.id) r.id = (accMatch && accMatch.id) || (portMatch && portMatch.cu) || '';
        if (!r.agent) r.agent = (accMatch && accMatch.agent) || (portMatch && portMatch.agent) || '';
        if (!r.client || r.client === 'undefined') r.client = (accMatch && accMatch.client) || (portMatch && portMatch.name) || '';
      });
      await ftdHook.replaceAll(data);
      showMsg(ll.uploadSuccess + ' (' + data.length + ' ' + ll.rows + ')');
    } catch (e) { showMsg(ll.uploadError); console.error(e); }
    setUploading(false);
  };

  var handleUploadLeads = async function(file) {
    setUploading(true);
    showMsg(ll.uploading);
    try {
      var data = await parseLeadsExcel(file);
      // Build set of existing phone keys
      var existingKeys = new Set();
      myLeads.forEach(function(ld) {
        var k = phoneKey(ld.phone);
        if (k) existingKeys.add(k);
      });
      // Filter out duplicates (within file and against existing)
      var seenInFile = new Set();
      var newData = [];
      var skipped = 0;
      var invalidPhone = 0;
      data.forEach(function(d) {
        var k = phoneKey(d.phone);
        if (!k) {
          // Phone has less than 9 digits - skip
          invalidPhone++;
          return;
        }
        if (existingKeys.has(k) || seenInFile.has(k)) {
          skipped++;
          return;
        }
        seenInFile.add(k);
        newData.push(Object.assign({}, d, { uploadedBy: RIVERPRIME_LEAD_SOURCE }));
      });
      if (newData.length > 0) {
        await leadsHook.bulkUpload(newData);
      }
      showMsg(ll.uploadSuccess + ' (' + newData.length + ' new, ' + skipped + ' duplicates skipped' + (invalidPhone > 0 ? ', ' + invalidPhone + ' invalid phone (<9 digits)' : '') + ')');
    } catch (e) { showMsg(ll.uploadError); console.error(e); }
    setUploading(false);
  };

  var handleUploadFollowUps = async function(file) {
    setUploading(true);
    showMsg(ll.uploading);
    try {
      var data = await parseFollowUpExcel(file);
      await followupsHook.bulkUpload(data);
      showMsg(ll.uploadSuccess + ' (' + data.length + ' comments imported)');
    } catch (e) { showMsg(ll.uploadError); console.error(e); }
    setUploading(false);
  };

  var handleUploadNotActive = async function(file) {
    setUploading(true);
    showMsg(ll.uploading);
    try {
      var data = await parseNotActiveExcel(file);
      await notActiveHook.replaceAll(data);
      showMsg(ll.uploadSuccess + ' (' + data.length + ' ' + ll.rows + ')');
    } catch (e) {
      showMsg(ll.uploadError);
      console.error(e);
    }
    setUploading(false);
  };

  var handleAddLead = async function() {
    if (!newLead.client || !newLead.phone || !newLead.agent) return;
    // Validate phone has at least 9 digits
    var digits = String(newLead.phone).replace(/\D/g, '');
    if (digits.length < 9) { showMsg('⚠️ Phone must have at least 9 digits!'); return; }
    // Check for duplicate phone
    var k = phoneKey(newLead.phone);
    if (k) {
      var exists = myLeads.some(function(ld) { return phoneKey(ld.phone) === k; });
      if (exists) { showMsg('⚠️ This phone number already exists!'); return; }
    }
    await leadsHook.addItem(Object.assign({}, newLead, { status: '', action: '', comments: '', uploadedBy: RIVERPRIME_LEAD_SOURCE }));
    setNewLead({ client: '', phone: '', agent: '', date: localNow().slice(0, 10) });
    setShowAddLead(false);
    showMsg(ll.leadAdded);
  };

  var handleCleanDuplicateLeads = async function() {
    var toDelete = [];
    var invalidCount = 0;
    var dupCount = 0;

    // 1. Find leads with invalid phone (<9 digits)
    myLeads.forEach(function(ld) {
      var digits = String(ld.phone || '').replace(/\D/g, '');
      if (digits.length < 9) {
        toDelete.push(ld._id);
        invalidCount++;
      }
    });

    // 2. Find duplicates among valid leads (group by last 9 digits, keep latest)
    var validLeads = myLeads.filter(function(ld) {
      var digits = String(ld.phone || '').replace(/\D/g, '');
      return digits.length >= 9;
    });
    var groups = {};
    validLeads.forEach(function(ld) {
      var k = phoneKey(ld.phone);
      if (!k) return;
      if (!groups[k]) groups[k] = [];
      groups[k].push(ld);
    });
    Object.keys(groups).forEach(function(k) {
      var arr = groups[k];
      if (arr.length <= 1) return;
      arr.sort(function(a, b) { return (b.date || '').localeCompare(a.date || ''); });
      for (var i = 1; i < arr.length; i++) {
        toDelete.push(arr[i]._id);
        dupCount++;
      }
    });

    if (toDelete.length === 0) {
      showMsg('✅ No invalid phones or duplicates found!');
      return;
    }
    if (!window.confirm('Found:\n• ' + invalidCount + ' leads with invalid phone (<9 digits)\n• ' + dupCount + ' duplicate leads\n\nTotal: ' + toDelete.length + ' leads to delete. Proceed?')) return;
    showMsg('Cleaning ' + toDelete.length + ' leads...');
    var batch = [];
    for (var i = 0; i < toDelete.length; i++) {
      batch.push(leadsHook.deleteItem(toDelete[i]));
      if (batch.length >= 10) {
        await Promise.all(batch);
        batch = [];
      }
    }
    if (batch.length > 0) await Promise.all(batch);
    showMsg('✅ Cleaned! ' + invalidCount + ' invalid phones + ' + dupCount + ' duplicates removed');
  };

  var handleCleanDuplicateFollowups = async function() {
    var rows = followupsHook.data || [];
    var byLogin = {};
    var toDelete = [];

    rows.forEach(function(fu) {
      var k = String(fu.login || '').trim();
      if (!k) return;
      if (!byLogin[k]) {
        byLogin[k] = fu;
        return;
      }
      var prev = byLogin[k];
      var prevTs = prev.updatedAt || prev.createdAt || '';
      var currTs = fu.updatedAt || fu.createdAt || '';
      if (currTs >= prevTs) {
        if (prev._id) toDelete.push(prev._id);
        byLogin[k] = fu;
      } else {
        if (fu._id) toDelete.push(fu._id);
      }
    });

    if (toDelete.length === 0) {
      showMsg('✅ No duplicate follow-ups found!');
      return;
    }

    if (!window.confirm('Found ' + toDelete.length + ' duplicate follow-up rows.\n\nKeep latest record per login and delete old duplicates?')) return;

    showMsg('Cleaning ' + toDelete.length + ' follow-up duplicates...');
    var batch = [];
    for (var i = 0; i < toDelete.length; i++) {
      batch.push(followupsHook.deleteItem(toDelete[i]));
      if (batch.length >= 10) {
        await Promise.all(batch);
        batch = [];
      }
    }
    if (batch.length > 0) await Promise.all(batch);
    showMsg('✅ Removed ' + toDelete.length + ' duplicate follow-up rows');
  };

  var handleExport = function(data, name) {
    try {
      var exportRows = Array.isArray(data) ? data : [];
      if (name === 'portfolio') {
        exportRows = exportRows.map(function(r) {
          return {
            MonthKey: r.monthKey || r.MonthKey || '',
            Login: r.login,
            Name: r.name,
            'T.Deposit': r.tDeposit,
            'T.Withdraw': r.tWithdraw,
            'T.Credit': r.tCredit,
            'T.Volume': r.tVolume,
            'T.Profit': r.tProfit,
            Deposit: r.deposit,
            Withdraw: r.withdraw,
            Credit: r.credit,
            Profit: r.profit,
            Equity: r.equity,
            FloatingPL: r.floatingPL,
            EquityPrevMonth: r.equityPrevMonth,
            First_Deposit: r.firstDeposit,
            Volume: r.volume,
            TotalDeals: r.totalDeals,
            TotalTrades: r.totalTrades,
            MostTradedInstruments: r.mostTradedInstruments,
            MostInstrument: r.mostInstrument,
            Country: r.country,
            CU_Account: r.cu,
            CRM_Account_Open_Date: r.crmOpenDate,
            Registration: r.registration,
            LastAccess: r.lastAccess,
            ClientStatus: r.clientStatus,
            IsReactivated: r.isReactivated || '',
            ReactivationDate: r.reactivationDate || '',
            Account_Type: r.accountType,
            ClientType: r.clientType,
            Phone_Number: r.phone || '',
            'A.M': r.agent,
          };
        });
      }
      exportToExcel(exportRows, name + '_' + localNow().slice(0, 10));
      showMsg('Exported');
    } catch (err) {
      console.error('Export failed:', err);
      showMsg('Export failed');
    }
  };

  // LOGIN SCREEN
  if (authLoading) return <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner th={{ border: '#333', accent: '#d4af37', sub: '#777' }} text="Loading..." /></div>;

  if (!userProfile) {
    return <LoginPage
      onLogin={login}
      lang={lang} setLang={setLang} ll={ll}
      thKey={thKey} setThKey={setThKey} />;
  }

  // Totals
  var totalDep = myPort.reduce(function(s, x) { return s + (Number(x.tDeposit) || 0); }, 0);
  var totalEq = myPort.reduce(function(s, x) { return s + (Number(x.equity) || 0); }, 0);
  var totalProf = myPort.reduce(function(s, x) { return s + (Number(x.tProfit) || 0); }, 0);
  var totalFtdAmt = (function() {
    var ym = latestFtdMonth || '';
    var filtered = ym ? myFtd.filter(function(x) { return (x.ftdDate || '').slice(0, 7) === ym; }) : myFtd;
    return filtered.reduce(function(s, x) { return s + (Number(x.ftdAmount) || 0); }, 0);
  })();

  var tabs = [
    { key: 'dashboard', label: ll.dashboard },
    { key: 'portfolio', label: ll.portfolio },
    { key: 'accounts', label: ll.newAccounts },
    { key: 'ftd', label: ll.ftd },
    { key: 'leads', label: ll.leads },
    { key: 'notactive', label: ll.notActive },
    { key: 'followup', label: ll.followUp },
    { key: 'retention', label: 'Retention' },
  ];
  if (role === 'admin') tabs.push({ key: 'users', label: ll.users });

  // Filters component shorthand
  var renderFilters = function(opts) {
    return <FiltersBar search={search} setSearch={setSearch} fAgent={fAgent} setFAgent={setFAgent}
      fStatus={opts.showStatus ? fStatus : []} setFStatus={opts.showStatus ? setFStatus : null}
      fAction={opts.showAction ? fAction : []} setFAction={opts.showAction ? setFAction : null}
      actions={opts.showAction ? ACTIONS : null} showAction={!!opts.showAction}
      extraFilters={opts.extraFilters || null}
      dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
      agents={role !== 'agent' ? allAgentNamesFromData : null} showAgent={role !== 'agent'}
      showStatus={opts.showStatus} ll={ll} th={th} rtl={rtl} onClear={resetF} />;
  };

  var hdrBtnStyle = {
    background: thKey === 'light' ? 'linear-gradient(180deg,#ffffff,#f1f5f9)' : 'linear-gradient(160deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 100%)',
    border: '1px solid ' + (thKey === 'light' ? 'rgba(148,163,184,0.38)' : 'rgba(255,255,255,0.20)'),
    borderRadius: 10,
    padding: '7px 13px',
    color: th.hText,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.2px',
    cursor: 'pointer',
    backdropFilter: 'blur(6px)',
    transition: 'transform .15s ease, filter .15s ease, border-color .15s ease, box-shadow .15s ease'
  };

  return (
    <CopyGuard role={role}>
      <div style={{ minHeight: '100vh', background: th.body, fontFamily: "'Manrope','Cairo','Segoe UI',sans-serif", direction: rtl ? 'rtl' : 'ltr' }}>
        {/* HEADER */}
        <header style={{
          background: thKey === 'light' ? 'linear-gradient(180deg,#ffffff,#f8fafc)' : 'linear-gradient(180deg,' + th.header + ', ' + th.header + ')',
          borderBottom: '1px solid ' + th.hBorder,
          position: 'sticky', top: 0, zIndex: 30,
          backdropFilter: 'blur(14px)',
          boxShadow: thKey === 'light' ? '0 1px 0 rgba(148,163,184,0.25), 0 12px 28px rgba(15,23,42,0.08)' : '0 1px 0 rgba(255,255,255,0.08), 0 12px 30px rgba(2,6,23,0.3)'
        }}>
          <div style={{ maxWidth: 1680, margin: '0 auto', display: 'flex', alignItems: 'center', minHeight: 78, padding: '8px 22px', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(180deg,#0a2540,#0d3060)', boxShadow: '0 10px 24px rgba(2,6,23,0.22)', border: '1px solid rgba(255,255,255,0.12)', transition: 'transform .2s ease, box-shadow .2s ease', cursor: 'default' }}
              onMouseEnter={function(e) { e.currentTarget.style.transform = 'scale(1.07)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(2,6,23,0.35)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(2,6,23,0.28)'; }}>
              <img src="/s.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6, boxSizing: 'border-box' }}
                onError={function(e) { e.target.parentNode.style.background = 'linear-gradient(135deg,#0f766e,#0ea5a2)'; e.target.style.display = 'none'; }} />
            </div>
            <div style={{ marginInlineEnd: 14, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ color: th.hText, fontWeight: 800, fontSize: 14, fontFamily: "'Manrope','Cairo',sans-serif", letterSpacing: '0.2px', lineHeight: 1.2 }}>{ll.company}</div>
              <div style={{ color: 'rgba(221,244,240,0.68)', fontSize: 9, letterSpacing: '1.4px', textTransform: 'uppercase', fontWeight: 600 }}>{ll.app}</div>
            </div>
            <div style={{ flex: 1 }} />
            <span style={{ background: role === 'admin' ? 'rgba(192,57,43,0.14)' : role === 'manager' ? 'rgba(212,175,55,0.11)' : 'rgba(22,131,94,0.11)', color: role === 'admin' ? '#e74c3c' : role === 'manager' ? '#d4af37' : '#1abc9c', border: '1px solid ' + (role === 'admin' ? 'rgba(192,57,43,0.3)' : role === 'manager' ? 'rgba(212,175,55,0.28)' : 'rgba(22,131,94,0.28)'), borderRadius: 20, padding: '5px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.45px', backdropFilter: 'blur(4px)' }}>
              {userProfile.name}
            </span>
            {role === 'agent' && (function() {
              var agentName = userProfile.dataAgentName || userProfile.name;
              var allNotifs = (notifsHook.data || []).filter(function(n) { return (n.agent === agentName || n.agentName === agentName) && !n.dismissed; });
              // Deduplicate display: keep only first (latest) per leadId
              var seen = {};
              var myNotifs = [];
              allNotifs.sort(function(a, b) { return (b.createdAt || '').localeCompare(a.createdAt || ''); });
              for (var ni = 0; ni < allNotifs.length; ni++) {
                var nn = allNotifs[ni];
                var dk = (nn.type || '') + '_' + (nn.leadId || nn._id) + '_' + (nn.fuId || '');
                if (!seen[dk]) { seen[dk] = true; myNotifs.push(nn); }
              }
              var unreadCount = myNotifs.length;
              return (
                <div style={{ position: 'relative' }}>
                  <button onClick={function() { setShowNotifDropdown(!showNotifDropdown); }}
                    style={{ background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.02))', border: '1px solid rgba(212,175,55,0.25)', borderRadius: 8, padding: '6px 10px', color: th.hText, fontSize: 16, cursor: 'pointer', position: 'relative', lineHeight: 1, backdropFilter: 'blur(4px)', transition: 'transform .15s ease, box-shadow .15s ease' }}>
                    🔔
                    {unreadCount > 0 && <span style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff', fontSize: 8, fontWeight: 800, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(231,76,60,0.5)', animation: 'pulse 1.5s infinite' }}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
                  </button>
                  {showNotifDropdown && (
                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 320, maxHeight: 400, overflowY: 'auto', background: th.card, borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', border: '1px solid ' + th.border, zIndex: 999 }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + th.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: th.text }}>🔔 Notifications</span>
                        {myNotifs.length > 0 && (
                          <button onClick={function() { allNotifs.forEach(function(n) { notifsHook.updateItem(n._id, { dismissed: true }); }); showMsg('All cleared'); }}
                            style={{ fontSize: 10, color: '#e74c3c', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear All</button>
                        )}
                      </div>
                      {myNotifs.length === 0 ? (
                        <div style={{ padding: 20, textAlign: 'center', color: th.sub, fontSize: 12 }}>No notifications</div>
                      ) : myNotifs.sort(function(a, b) { return (b.createdAt || '').localeCompare(a.createdAt || ''); }).map(function(n) {
                        return (
                          <div key={n._id} onClick={function() {
                            var targetTab = n.type === 'reminder' && n.fuId ? 'followup' : 'leads';
                            var targetId = n.fuId || n.leadId || '';
                            var clientName = (n.title || '').replace(/^(Reminder|New Lead): /, '');
                            setTab(targetTab); setShowNotifDropdown(false);
                            setSearch(clientName);
                            setHighlightId(targetId);
                            setTimeout(function() { setHighlightId(null); }, 300000);
                            notifsHook.updateItem(n._id, { dismissed: true });
                          }} style={{ padding: '10px 16px', borderBottom: '1px solid ' + th.border + '44', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}
                            onMouseOver={function(e) { e.currentTarget.style.background = th.stripe; }}
                            onMouseOut={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                            <span style={{ fontSize: 20, lineHeight: 1 }}>{n.type === 'new_lead' ? '🎯' : '🔔'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: th.text }}>{n.title}</div>
                              {n.message && <div style={{ fontSize: 10, color: th.sub, marginTop: 2 }}>{n.message}</div>}
                              <div style={{ fontSize: 9, color: th.sub, marginTop: 3 }}>{localFmt(n.createdAt)}</div>
                            </div>
                            <button onClick={function(e) { e.stopPropagation(); notifsHook.updateItem(n._id, { dismissed: true }); }}
                              style={{ background: 'none', border: 'none', color: th.sub, fontSize: 14, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ width: 1, height: 24, background: thKey === 'light' ? 'rgba(148,163,184,0.45)' : 'rgba(255,255,255,0.18)', flexShrink: 0 }} />
            <select value={thKey} onChange={function(e) { setThKey(e.target.value); }} style={Object.assign({}, hdrBtnStyle, { padding: '6px 8px' })}>
              <option value="dark" style={{ color: '#000' }}>Dark</option>
              <option value="light" style={{ color: '#000' }}>Light</option>
              <option value="hybrid" style={{ color: '#000' }}>Hybrid</option>
            </select>
            <button onClick={function() { setLang(lang === 'en' ? 'ar' : 'en'); }} style={hdrBtnStyle}>{ll.lang}</button>
            <button onClick={function() { logout(); resetF(); setTab('dashboard'); setUsersLoaded(false); }} style={Object.assign({}, hdrBtnStyle, { borderColor: 'rgba(192,57,43,0.4)', color: '#e0705e' })}>{ll.logout}</button>
          </div>
        </header>

        <div style={{ maxWidth: 1680, margin: '0 auto', padding: '14px 16px' }}>
          {/* TABS */}
          <div style={{ display: 'flex', gap: 6, background: 'linear-gradient(180deg, ' + th.card + ', ' + th.stripe + ')', borderRadius: 14, padding: 6, marginBottom: 16, border: '1px solid ' + th.border, overflowX: 'auto', width: 'fit-content', boxShadow: '0 12px 28px rgba(15,23,42,0.08)' }}>
            {tabs.map(function(t) {
              var badge = (t.key === 'leads' && role === 'agent' && unseenLeadIds.length > 0) ? unseenLeadIds.length : 0;
              return <button key={t.key} onClick={function() {
                setTab(t.key);
                if (t.key === 'leads') { setUnseenLeadIds([]); }
              }} style={{ padding: '9px 15px', borderRadius: 10, border: tab === t.key ? '1px solid transparent' : ('1px solid ' + th.border + '88'), cursor: 'pointer', fontSize: 11, fontWeight: 700, background: tab === t.key ? th.tabA : 'transparent', color: tab === t.key ? th.tabAT : th.sub, whiteSpace: 'nowrap', position: 'relative', transition: 'all .14s ease', boxShadow: tab === t.key ? '0 10px 22px rgba(2,6,23,0.2)' : 'none' }}>
                {t.label}
                {badge > 0 && <span style={{ position: 'absolute', top: -4, right: -4, background: '#e74c3c', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(231,76,60,0.5)', animation: 'pulse 1.5s infinite' }}>{badge}</span>}
              </button>;
            })}
          </div>

          {/* UPLOAD BAR */}
          {(role === 'admin' || role === 'manager') && ['portfolio', 'accounts', 'ftd', 'leads', 'followup', 'notactive'].indexOf(tab) >= 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', background: th.card, border: '1px solid ' + th.border, borderRadius: 10, padding: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.06)' }}>
              {tab === 'portfolio' && role === 'admin' && <UploadButton label={ll.uploadPortfolio} bg="#6366f1" color="white" onFile={handleUploadPortfolio} />}
              {tab === 'accounts' && role === 'admin' && <UploadButton label={ll.uploadAccounts} bg="#2b6cb0" color="white" onFile={handleUploadAccounts} />}
              {tab === 'ftd' && role === 'admin' && <UploadButton label={ll.uploadFTD} bg="#16835e" color="white" onFile={handleUploadFTD} />}
              {tab === 'leads' && <UploadButton label={ll.bulkUpload} bg="#16835e" color="white" onFile={handleUploadLeads} />}

              {tab === 'leads' && role === 'admin' && (
                <button onClick={handleCleanDuplicateLeads} style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  🧹 Clean Phone Numbers
                </button>
              )}
              {tab === 'followup' && role === 'admin' && <UploadButton label="📋 Import Comments" bg="#f59e0b" color="white" onFile={handleUploadFollowUps} />}
              {tab === 'followup' && role === 'admin' && (
                <button onClick={handleCleanDuplicateFollowups} style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  🧹 Clean Follow-Up Duplicates
                </button>
              )}
              {tab === 'leads' && (
                <button onClick={function() { downloadLeadsTemplate(); }} style={{ background: th.accent, color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {'↓ ' + ll.downloadTpl}
                </button>
              )}
              {role === 'admin' && tab !== 'followup' && (
                <button onClick={function() {
                  var d = tab === 'portfolio' ? portAggregated : tab === 'accounts' ? myAcc2026 : tab === 'ftd' ? myFtd : tab === 'notactive' ? (notActiveHook.data || []) : myLeadsDeduped;
                  handleExport(d, tab);
                }} style={{ background: '#1a1a1a', color: '#d4af37', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginInlineStart: 'auto' }}>
                  {'↓ ' + ll.exportData}
                </button>
              )}
              {uploading && <Spinner th={th} text={ll.uploading} />}
            </div>
          )}

          {/* ═══════ DASHBOARD ═══════ */}
          {tab === 'dashboard' && (function() {
            if (dashboardSummaryLoading && !dashboardSummary && !portfolioData.length) {
              return <Spinner th={th} text={ll.loading} />;
            }
            // Use current month data for all stats
            var cm = myPortCurrentMonth;
            var uniqueCU = new Set(cm.map(function(x) { return x.cu; })).size;
            var uniqueLogins = new Set(cm.map(function(x) { return x.login; })).size;
            var activeCU = new Set(cm.filter(function(x) { return x.clientStatus === 'Active'; }).map(function(x) { return x.cu; })).size;
            var totalDepAmt = cm.reduce(function(s,x){ return s+(Number(x.deposit)||0); },0);
            var totalWdAmt = cm.reduce(function(s,x){ return s+(Number(x.withdraw)||0); },0);
            var totalVolAmt = cm.reduce(function(s,x){ return s+(Number(x.volume)||0); },0);
            var fmt$ = function(v) { return '$' + (Number(v)||0).toLocaleString(undefined,{maximumFractionDigits:0}); };
            var fmtN = function(v) { return (Number(v)||0).toLocaleString(undefined,{maximumFractionDigits:0}); };
            var monthTag = latestMonthLabel ? ' (' + latestMonthLabel + ')' : '';
            var ftdTag = latestFtdLabel ? ' (' + latestFtdLabel + ')' : '';
            var accTag = latestAccLabel ? ' (' + latestAccLabel + ')' : '';
            var summary = dashboardSummary && dashboardSummary.summary ? dashboardSummary.summary : null;
            var chartsSummary = dashboardSummary && dashboardSummary.charts ? dashboardSummary.charts : null;
            var totalFtdAmtLocal = totalFtdAmt;
            var newLoginsCountLocal = newLoginsCount;
            var newClientsCountLocal = newClientsCount;
            var leadsCountLocal = myLeadsDeduped.length;
            var retentionCountLocal = myRetention.length;
            var followupsCountLocal = (followupsHook.data.filter(function(f) { return f.agent === (userProfile.dataAgentName || userProfile.name); })).length;
            var accChartData = accChart;
            var volChartData = volChart;
            var retentionChartData = retentionChart;
            var depVsWdData = depVsWd;
            var ftdChartData = ftdChart;
            var nonFundedChartData = nonFundedChart;
            var leadsChartData = leadsChart;

            // Client status data (current month)
            var statusMap = {};
            var cuSeen = {};
            cm.forEach(function(p) {
              var st = p.clientStatus || 'Unknown';
              if (!cuSeen[p.cu + '_' + st]) {
                cuSeen[p.cu + '_' + st] = true;
                statusMap[st] = (statusMap[st] || 0) + 1;
              }
            });
            var statusColors = { 'Active': '#16835e', 'Not Active': '#e74c3c', 'Dormant': '#f59e0b' };
            var clientStatusChart = Object.keys(statusMap).map(function(k) {
              return { name: k, value: statusMap[k], fill: statusColors[k] || '#6366f1' };
            });

            if (summary) {
              uniqueCU = Number(summary.uniqueCU || 0);
              uniqueLogins = Number(summary.uniqueLogins || 0);
              activeCU = Number(summary.activeCU || 0);
              totalDepAmt = Number(summary.totalDepAmt || 0);
              totalWdAmt = Number(summary.totalWdAmt || 0);
              totalVolAmt = Number(summary.totalVolAmt || 0);
              totalFtdAmtLocal = Number(summary.totalFtdAmt || 0);
              newLoginsCountLocal = Number(summary.newLoginsCount || 0);
              newClientsCountLocal = Number(summary.newClientsCount || 0);
              leadsCountLocal = Number(summary.leadsCount || 0);
              retentionCountLocal = Number(summary.retentionCount || 0);
              followupsCountLocal = Number(summary.followupsCount || 0);
              monthTag = summary.latestMonthLabel ? ' (' + summary.latestMonthLabel + ')' : '';
              ftdTag = summary.latestFtdLabel ? ' (' + summary.latestFtdLabel + ')' : '';
              accTag = summary.latestAccLabel ? ' (' + summary.latestAccLabel + ')' : '';
            }

            if (chartsSummary) {
              accChartData = chartsSummary.accChart || [];
              volChartData = chartsSummary.volChart || [];
              retentionChartData = chartsSummary.retentionChart || [];
              depVsWdData = chartsSummary.depVsWd || [];
              ftdChartData = chartsSummary.ftdChart || [];
              nonFundedChartData = chartsSummary.nonFundedChart || [];
              clientStatusChart = chartsSummary.clientStatusChart || [];
              leadsChartData = chartsSummary.leadsChart || [];
            }

            // ═══ AGENT DASHBOARD — Big Number Cards Only (Option B) ═══
            if (role === 'agent') {
              var agentCards = [
                { icon: '👥', label: ll.totalClients + monthTag, value: fmtN(uniqueCU), color: '#6366f1' },
                { icon: '✅', label: ll.activeClients + monthTag, value: fmtN(activeCU), color: '#16835e' },
                { icon: '🔑', label: ll.totalLogins + monthTag, value: fmtN(uniqueLogins), color: '#2b6cb0' },
                { icon: '🔄', label: ll.totalVolume + monthTag, value: fmtN(totalVolAmt), color: '#8b5cf6' },
                { icon: '💰', label: ll.totalDeposits + monthTag, value: fmt$(totalDepAmt), color: '#16835e' },
                { icon: '💸', label: 'Withdrawals' + monthTag, value: fmt$(totalWdAmt), color: '#e74c3c' },
                { icon: '🏆', label: ll.ftdTotal + ftdTag, value: fmt$(totalFtdAmtLocal), color: '#f59e0b' },
                { icon: '🚫', label: 'Non Funded', value: fmtN(totalNonFunded), color: '#e74c3c' },
                { icon: '🆕', label: ll.newLogins + accTag, value: fmtN(newLoginsCountLocal), color: '#0ea5e9' },
                { icon: '👤', label: ll.newClients + accTag, value: fmtN(newClientsCountLocal), color: '#6366f1' },
                { icon: '📋', label: ll.totalLeads, value: fmtN(leadsCountLocal), color: '#e74c3c' },
                { icon: '📞', label: ll.followUp, value: fmtN(followupsCountLocal), color: '#f97316' },
                { icon: '🔁', label: 'Retention' + monthTag, value: fmtN(retentionCountLocal), color: '#0ea5e9' },
              ];
              var renderCard = function(c, i) {
                var cardGrad = thKey === 'dark'
                  ? 'linear-gradient(145deg, #172334 0%, #0f1a2a 100%)'
                  : thKey === 'hybrid'
                    ? 'linear-gradient(145deg, #123346 0%, #1f5a56 100%)'
                    : 'linear-gradient(145deg, #ffffff 0%, #f3f8fc 100%)';
                var cardBorder = thKey === 'dark' ? 'rgba(86,122,151,0.45)' : thKey === 'hybrid' ? 'rgba(76,174,162,0.38)' : 'rgba(148,163,184,0.35)';
                var glow = thKey === 'dark'
                  ? '0 16px 34px rgba(2,8,23,0.45)'
                  : thKey === 'hybrid'
                    ? '0 16px 34px rgba(6,37,44,0.32)'
                    : '0 14px 28px rgba(15,23,42,0.12)';
                var labelColor = thKey === 'dark' ? '#8ca4bc' : thKey === 'hybrid' ? '#c9ece7' : '#64748b';
                var iconBg = thKey === 'light' ? 'rgba(15,118,110,0.08)' : 'rgba(255,255,255,0.10)';
                return (
                  <div key={i} style={{
                    background: cardGrad,
                    border: '1px solid ' + cardBorder,
                    borderRadius: 18,
                    padding: '18px 16px',
                    textAlign: 'left',
                    boxShadow: glow,
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 122,
                  }}>
                    <div style={{
                      position: 'absolute',
                      right: -22,
                      top: -22,
                      width: 96,
                      height: 96,
                      borderRadius: '50%',
                      background: c.color + '22',
                      filter: 'blur(2px)'
                    }} />
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: labelColor, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                          {c.label}
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 900, color: c.color, lineHeight: 1.06, fontFamily: 'Manrope,Cairo,sans-serif' }}>
                          {c.value}
                        </div>
                      </div>
                      <div style={{
                        width: 38,
                        height: 38,
                        borderRadius: 11,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 19,
                        background: iconBg,
                        border: '1px solid ' + (thKey === 'light' ? 'rgba(15,118,110,0.18)' : 'rgba(255,255,255,0.16)')
                      }}>
                        {c.icon}
                      </div>
                    </div>
                  </div>
                );
              };
              return (
                <div style={{ marginTop: 2 }}>
                  <div style={{
                    marginBottom: 14,
                    padding: '14px 16px',
                    borderRadius: 14,
                    background: thKey === 'dark' ? 'linear-gradient(135deg, rgba(42,161,152,0.18), rgba(14,116,144,0.12))' : 'linear-gradient(135deg, rgba(15,118,110,0.12), rgba(14,116,144,0.09))',
                    border: '1px solid ' + (thKey === 'dark' ? 'rgba(66,175,167,0.35)' : 'rgba(15,118,110,0.25)')
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: th.text }}>{ll.dashboard}</div>
                    <div style={{ fontSize: 11, marginTop: 2, color: th.sub }}>Your latest performance snapshot</div>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                    gap: 12,
                    alignItems: 'stretch'
                  }}>
                    {agentCards.map(renderCard)}
                  </div>
                </div>
              );
            }

            // ═══ MANAGER/ADMIN DASHBOARD — Option A (Stats + Charts) ═══
            return (
            <div>
              <div style={{
                marginBottom: 14,
                padding: '14px 16px',
                borderRadius: 14,
                background: thKey === 'dark' ? 'linear-gradient(135deg, rgba(42,161,152,0.18), rgba(14,116,144,0.12))' : 'linear-gradient(135deg, rgba(15,118,110,0.10), rgba(14,116,144,0.08))',
                border: '1px solid ' + (thKey === 'dark' ? 'rgba(66,175,167,0.35)' : 'rgba(15,118,110,0.22)')
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: th.text }}>{ll.dashboard}</div>
                <div style={{ fontSize: 11, marginTop: 2, color: th.sub }}>Portfolio, revenue, and team performance overview</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
                <StatCard label={ll.totalClients + monthTag} value={fmtN(uniqueCU)} color="#6366f1" th={th} note={excludedCUCount > 0 ? '-' + excludedCUCount + ' excluded' : ''} />
                <StatCard label={ll.totalLogins + monthTag} value={fmtN(uniqueLogins)} color="#2b6cb0" th={th} note={excludedPortCount > 0 ? '-' + excludedPortCount + ' excluded' : ''} />
                <StatCard label={ll.activeClients + monthTag} value={fmtN(activeCU)} color="#16835e" th={th} />
                <StatCard label={'🔁 Retention' + monthTag} value={fmtN(retentionCountLocal)} color="#0ea5e9" th={th} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label={'Volume' + monthTag} value={fmtN(totalVolAmt)} color="#8b5cf6" th={th} />
                <StatCard label={ll.ftdTotal + ftdTag} value={fmt$(totalFtdAmtLocal)} color="#f59e0b" th={th} />
                <StatCard label={ll.newLogins + accTag} value={fmtN(newLoginsCountLocal)} color="#0ea5e9" th={th} />
                <StatCard label={ll.newClients + accTag} value={fmtN(newClientsCountLocal)} color="#8b5cf6" th={th} />
                <StatCard label={ll.totalLeads} value={fmtN(leadsCountLocal)} color="#e74c3c" th={th} />
              </div>
              {/* ROW 1 (3): New Accounts + Volume + Retention */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 14 }}>
                <ChartCard title={ll.chartAccounts + (latestAccLabel ? ' (' + latestAccLabel + ')' : '')} th={th}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={accChartData}><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis dataKey="name" tick={{ fontSize: 9, fill: th.sub }} /><YAxis tick={{ fontSize: 9, fill: th.sub }} allowDecimals={false} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} /><Legend wrapperStyle={{ fontSize: 10 }} /><Bar dataKey="logins" name="Logins" fill="#0ea5e9" radius={[6, 6, 0, 0]} /><Bar dataKey="clients" name="Clients" fill="#8b5cf6" radius={[6, 6, 0, 0]} /></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title={'Volume by Agent' + (latestMonthLabel ? ' (' + latestMonthLabel + ')' : '')} th={th}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={volChartData}><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis dataKey="name" tick={{ fontSize: 9, fill: th.sub }} /><YAxis tick={{ fontSize: 9, fill: th.sub }} tickFormatter={function(v) { return v.toLocaleString(); }} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} formatter={function(v) { return v.toLocaleString(); }} /><Bar dataKey="val" radius={[6, 6, 0, 0]}>{volChartData.map(function(e, i) { return <Cell key={i} fill={e.fill || CHART_COLORS[i % 10]} />; })}</Bar></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title={'Retention by Agent' + (latestMonthLabel ? ' (' + latestMonthLabel + ')' : '')} th={th}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={retentionChartData}><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis dataKey="name" tick={{ fontSize: 9, fill: th.sub }} /><YAxis tick={{ fontSize: 9, fill: th.sub }} allowDecimals={false} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} /><Bar dataKey="val" radius={[6, 6, 0, 0]}>{retentionChartData.map(function(e, i) { return <Cell key={i} fill={e.fill || CHART_COLORS[i % 10]} />; })}</Bar></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
              {/* ROW 2 (3): Deposits vs Withdrawals + FTD + Non Funded */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 14 }}>
                <ChartCard title={ll.depositVsWithdraw + (latestMonthLabel ? ' (' + latestMonthLabel + ')' : '')} th={th}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={depVsWdData}><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis dataKey="name" tick={{ fontSize: 9, fill: th.sub }} /><YAxis tick={{ fontSize: 9, fill: th.sub }} tickFormatter={function(v) { return '$' + v.toLocaleString(); }} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} formatter={function(v) { return '$' + v.toLocaleString(); }} /><Legend wrapperStyle={{ fontSize: 10 }} /><Bar dataKey="dep" fill="#16835e" radius={[6, 6, 0, 0]} name={ll.deposit} /><Bar dataKey="wd" fill="#e74c3c" radius={[6, 6, 0, 0]} name={ll.withdraw} /></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title={ll.chartFTD + (latestFtdLabel ? ' (' + latestFtdLabel + ')' : '')} th={th}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={ftdChartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis type="number" tick={{ fontSize: 9, fill: th.sub }} tickFormatter={function(v) { return '$' + v.toLocaleString(); }} /><YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: th.sub }} width={55} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} formatter={function(v) { return '$' + v.toLocaleString(); }} /><Bar dataKey="val" radius={[0, 6, 6, 0]}>{ftdChartData.map(function(e, i) { return <Cell key={i} fill={e.fill || CHART_COLORS[i % 10]} />; })}</Bar></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title={'Non Funded by Agent'} th={th}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={nonFundedChartData}><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis dataKey="name" tick={{ fontSize: 9, fill: th.sub }} /><YAxis tick={{ fontSize: 9, fill: th.sub }} allowDecimals={false} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} /><Bar dataKey="val" radius={[6, 6, 0, 0]}>{nonFundedChartData.map(function(e, i) { return <Cell key={i} fill={e.fill || CHART_COLORS[i % 10]} />; })}</Bar></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
              {/* ROW 3 (2): Client Status + Leads */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
                <ChartCard title={ll.clientStatusChart} th={th}>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart><Pie data={clientStatusChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3}>{clientStatusChart.map(function(e, i) { return <Cell key={i} fill={e.fill} />; })}</Pie><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} /><Legend wrapperStyle={{ fontSize: 10 }} /></PieChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title={ll.chartLeads} th={th}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={leadsChartData}><CartesianGrid strokeDasharray="3 3" stroke={th.cGrid} /><XAxis dataKey="name" tick={{ fontSize: 9, fill: th.sub }} /><YAxis tick={{ fontSize: 9, fill: th.sub }} allowDecimals={false} /><Tooltip contentStyle={tipStyle} labelStyle={{ color: th.text }} itemStyle={{ color: th.text }} /><Bar dataKey="val" radius={[6, 6, 0, 0]}>{leadsChartData.map(function(e, i) { return <Cell key={i} fill={e.fill || CHART_COLORS[i % 10]} />; })}</Bar></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
            );
          })()}

          {/* ═══════ PORTFOLIO ═══════ */}
          {tab === 'portfolio' && (function() {
            if (portfolioLoading) return <Spinner th={th} text={ll.loading} />;
            // Search filter on aggregated data
            var pFiltered = portAggregated.filter(function(r) {
              if (!search) return true;
              var s = search.toLowerCase();
              return (r.cu && r.cu.toLowerCase().indexOf(s) >= 0) ||
                     (r.name && r.name.toLowerCase().indexOf(s) >= 0) ||
                     (String(r.login).indexOf(s) >= 0) ||
                     (r.agent && r.agent.toLowerCase().indexOf(s) >= 0);
            });
            if (pFunded.length) {
              var ftdLoginSet = new Set();
              myFtd.forEach(function(f) { if (String(f.login || '')) ftdLoginSet.add(String(f.login)); });
              if (pFunded.indexOf('Funded') >= 0 && pFunded.indexOf('Non Funded') < 0) {
                pFiltered = pFiltered.filter(function(r) { return ftdLoginSet.has(String(r.login || '')); });
              } else if (pFunded.indexOf('Non Funded') >= 0 && pFunded.indexOf('Funded') < 0) {
                pFiltered = pFiltered.filter(function(r) { return !ftdLoginSet.has(String(r.login || '')); });
              }
            }
            // Recalculate stats from filtered data
            var lastAcc = '';
            pFiltered.forEach(function(r) {
              if (r.lastAccess && r.lastAccess > lastAcc) lastAcc = r.lastAccess;
            });
            var ps = {
              clients: new Set(pFiltered.map(function(x) { return x.cu; })).size,
              logins: pFiltered.length,
              deposit: pFiltered.reduce(function(s, x) { return s + x.deposit; }, 0),
              withdraw: pFiltered.reduce(function(s, x) { return s + x.withdraw; }, 0),
              volume: pFiltered.reduce(function(s, x) { return s + x.volume; }, 0),
              profit: pFiltered.reduce(function(s, x) { return s + x.profit; }, 0),
              credit: pFiltered.reduce(function(s, x) { return s + x.credit; }, 0),
              equity: pFiltered.reduce(function(s, x) { return s + x.equity; }, 0),
              floatingPL: pFiltered.reduce(function(s, x) { return s + x.floatingPL; }, 0),
              equityPrevMonth: pFiltered.reduce(function(s, x) { return s + x.equityPrevMonth; }, 0),
              totalDeals: pFiltered.reduce(function(s, x) { return s + x.totalDeals; }, 0),
              totalTrades: pFiltered.reduce(function(s, x) { return s + x.totalTrades; }, 0),
              lastAccess: lastAcc,
            };
            // T-totals from pFiltered (each row already has correct gLatest T-values)
            var tTotals = {
              tDeposit: pFiltered.reduce(function(s, x) { return s + x.tDeposit; }, 0),
              tWithdraw: pFiltered.reduce(function(s, x) { return s + x.tWithdraw; }, 0),
              tVolume: pFiltered.reduce(function(s, x) { return s + x.tVolume; }, 0),
              tProfit: pFiltered.reduce(function(s, x) { return s + x.tProfit; }, 0),
              tCredit: pFiltered.reduce(function(s, x) { return s + x.tCredit; }, 0),
            };
            // Build unique options from ALL data (myPort) so options always available
            var cuOpts = []; var loginOpts = []; var nameOpts = [];
            var cuSet = new Set(); var loginSet = new Set(); var nameSet = new Set();
            myPort.forEach(function(r) {
              if (r.cu && !cuSet.has(r.cu)) { cuSet.add(r.cu); cuOpts.push(r.cu); }
              if (r.login && !loginSet.has(String(r.login))) { loginSet.add(String(r.login)); loginOpts.push(String(r.login)); }
              var nm = r.name ? r.name.trim().replace(/\s+/g, ' ') : '';
              if (nm && nm !== 'NULL' && !nameSet.has(nm)) { nameSet.add(nm); nameOpts.push(nm); }
            });
            cuOpts.sort(); loginOpts.sort(); nameOpts.sort();
            var fmt = function(v) { return '$' + (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
            var fmtN = function(v) { return (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); };
            return (
              <div>
                {/* FILTERS ROW 1 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: '1 1 160px', minWidth: 140, position: 'relative' }}>
                    <input placeholder={ll.search} value={search} onChange={function(e) { setSearch(e.target.value); }}
                      style={{ width: '100%', padding: '8px 10px 8px 28px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, color: th.inpT, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                    <span style={{ position: 'absolute', left: rtl ? 'auto' : 9, right: rtl ? 9 : 'auto', top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: th.sub, pointerEvents: 'none' }}>&#8981;</span>
                  </div>
                  {role !== 'agent' && <div style={{ minWidth: 140 }}><MultiDropdown value={fAgent} options={allAgentNamesFromData} onChange={function(v) { setFAgent(v); }} placeholder={ll.allAgents} th={th} /></div>}
                  <div style={{ minWidth: 120 }}><MultiDropdown value={pMonth} options={portFilterOpts.months} onChange={function(v) { setPMonth(v); }} placeholder="All Months" th={th} /></div>
                  <div style={{ minWidth: 100 }}><MultiDropdown value={pYear} options={portFilterOpts.years} onChange={function(v) { setPYear(v); }} placeholder="All Years" th={th} /></div>
                  <div style={{ minWidth: 120 }}><MultiDropdown value={pClientStatus} options={portFilterOpts.statuses} onChange={function(v) { setPClientStatus(v); }} placeholder="All Status" th={th} /></div>
                  <div style={{ minWidth: 130 }}><MultiDropdown value={pAccType} options={portFilterOpts.accTypes} onChange={function(v) { setPAccType(v); }} placeholder="All Account Types" th={th} /></div>
                </div>
                {/* FILTERS ROW 2 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <div style={{ minWidth: 140 }}><MultiDropdown value={pCountry} options={portFilterOpts.countries} onChange={function(v) { setPCountry(v); }} placeholder="All Countries" th={th} /></div>
                  <div style={{ minWidth: 130 }}><MultiDropdown value={pFunded} options={['Funded', 'Non Funded']} onChange={function(v) { setPFunded(v); }} placeholder="All (FTD)" th={th} /></div>
                  <div style={{ minWidth: 130 }}><MultiDropdown value={pClientType} options={portFilterOpts.clientTypes} onChange={function(v) { setPClientType(v); }} placeholder="All Client Types" th={th} /></div>
                  <div style={{ minWidth: 130 }}><MultiDropdown value={pInstrument} options={portFilterOpts.instruments} onChange={function(v) { setPInstrument(v); }} placeholder="All Instruments" th={th} /></div>
                  <div style={{ minWidth: 130 }}><MultiDropdown value={pMostTraded} options={portFilterOpts.mostTraded} onChange={function(v) { setPMostTraded(v); }} placeholder="All Most Traded" th={th} /></div>
                </div>
                {/* FILTERS ROW 3 - CU, Login, Client Name */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div style={{ minWidth: 140 }}><MultiDropdown value={pCU} options={cuOpts} onChange={function(v) { setPCU(v); }} placeholder="CU Account" th={th} /></div>
                  <div style={{ minWidth: 130 }}><MultiDropdown value={pLogin} options={loginOpts} onChange={function(v) { setPLogin(v); }} placeholder="Login" th={th} /></div>
                  <div style={{ minWidth: 160 }}><MultiDropdown value={pClientName} options={nameOpts} onChange={function(v) { setPClientName(v); }} placeholder="Client Name" th={th} /></div>
                  <button onClick={resetF} style={{ padding: '8px 14px', borderRadius: 8, background: '#e74c3c', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Clear</button>
                  {role === 'admin' && <button onClick={function() { setShowExcludePanel(!showExcludePanel); }} style={{ padding: '8px 14px', borderRadius: 8, background: showExcludePanel ? '#f59e0b' : '#fef3c7', color: showExcludePanel ? '#fff' : '#92400e', border: '1px solid #f59e0b', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>{'🚫 Exclude' + (excludedCUs.length + excludedLogins.length > 0 ? ' (' + (excludedCUs.length + excludedLogins.length) + ')' : '')}</button>}
                </div>
                {/* EXCLUDE PANEL */}
                {showExcludePanel && role === 'admin' && (
                  <div style={{ background: th.card, border: '2px solid #f59e0b', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>🚫 Exclude from Calculations</span>
                      <button onClick={function() { setShowExcludePanel(false); }} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: th.sub }}>✕</button>
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                      <button onClick={function() { setExcludeMode('cu'); }} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid ' + (excludeMode === 'cu' ? '#f59e0b' : th.border), background: excludeMode === 'cu' ? '#f59e0b' : th.card, color: excludeMode === 'cu' ? '#fff' : th.sub, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>By CU Account</button>
                      <button onClick={function() { setExcludeMode('login'); }} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid ' + (excludeMode === 'login' ? '#2b6cb0' : th.border), background: excludeMode === 'login' ? '#2b6cb0' : th.card, color: excludeMode === 'login' ? '#fff' : th.sub, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>By Login</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 3 }}>{excludeMode === 'cu' ? 'CU Number' : 'Login Number'}</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input value={excludeInput} onChange={function(e) { setExcludeInput(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') handleAddExclude(); }} placeholder={excludeMode === 'cu' ? 'e.g. CU14101' : 'e.g. 100201'} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, color: th.inpT, fontSize: 12, width: 140, outline: 'none' }} />
                          <button onClick={handleAddExclude} style={{ padding: '7px 12px', borderRadius: 6, background: excludeMode === 'cu' ? '#f59e0b' : '#2b6cb0', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 3 }}>Bulk (comma or newline separated)</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <textarea value={excludeBulk} onChange={function(e) { setExcludeBulk(e.target.value); }} placeholder={excludeMode === 'cu' ? 'CU14101, CU8889...' : '100201, 100210...'} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, color: th.inpT, fontSize: 11, width: 240, height: 34, resize: 'vertical', outline: 'none', fontFamily: 'monospace' }} />
                          <button onClick={handleBulkExclude} style={{ padding: '7px 12px', borderRadius: 6, background: excludeMode === 'cu' ? '#f59e0b' : '#2b6cb0', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Bulk</button>
                        </div>
                      </div>
                      {(excludedCUs.length > 0 || excludedLogins.length > 0) && <button onClick={clearAllExclusions} style={{ padding: '7px 12px', borderRadius: 6, background: '#fee2e2', color: '#e74c3c', border: '1px solid #fca5a5', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑 Clear All</button>}
                    </div>
                    {excludedCUs.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: th.sub, marginBottom: 4 }}>Excluded CU Accounts ({excludedCUs.length}):</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <tbody>
                            {excludedCUs.map(function(cu) {
                              var loginCount = myPortRaw.filter(function(p) { return (p.cu || '').toUpperCase() === cu; }).length;
                              return <tr key={cu} style={{ borderBottom: '1px solid ' + th.border }}>
                                <td style={{ padding: '4px 8px', fontWeight: 600, color: '#92400e' }}>{cu}</td>
                                <td style={{ padding: '4px 8px', color: th.sub }}>{loginCount + ' logins'}</td>
                                <td style={{ padding: '4px 4px', textAlign: 'right' }}><button onClick={function() { removeExcludeCU(cu); }} style={{ background: '#fee2e2', color: '#e74c3c', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Remove</button></td>
                              </tr>;
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {excludedLogins.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: th.sub, marginBottom: 4 }}>Excluded Logins ({excludedLogins.length}):</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <tbody>
                            {excludedLogins.map(function(login) {
                              var match = myPortRaw.find(function(p) { return String(p.login || '') === login; });
                              return <tr key={login} style={{ borderBottom: '1px solid ' + th.border }}>
                                <td style={{ padding: '4px 8px', fontWeight: 600, color: '#1e40af' }}>{login}</td>
                                <td style={{ padding: '4px 8px', color: th.sub }}>{match ? match.cu : ''}</td>
                                <td style={{ padding: '4px 8px', color: th.sub }}>{match ? match.name : ''}</td>
                                <td style={{ padding: '4px 4px', textAlign: 'right' }}><button onClick={function() { removeExcludeLogin(login); }} style={{ background: '#fee2e2', color: '#e74c3c', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Remove</button></td>
                              </tr>;
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!excludedCUs.length && !excludedLogins.length && <div style={{ fontSize: 11, color: th.sub, fontStyle: 'italic' }}>No exclusions set. Add CU numbers or Logins above.</div>}
                  </div>
                )}
                {portfolioLastUpdate && <div style={{ fontSize: 10, color: th.sub, marginBottom: 8 }}>{'Last updated: ' + localFmt(portfolioLastUpdate) + ' | ' + pFiltered.length + ' logins'}</div>}
                {/* STAT BOXES - 2 rows */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <StatCard label="Total Clients" value={ps.clients} color="#6366f1" th={th} note={excludedCUCount > 0 ? '-' + excludedCUCount + ' excluded' : ''} />
                  <StatCard label="Total Logins" value={ps.logins} color="#2b6cb0" th={th} note={excludedPortCount > 0 ? '-' + excludedPortCount + ' excluded' : ''} />
                  <StatCard label="T.Deposit" value={fmt(tTotals.tDeposit)} color="#16835e" th={th} />
                  <StatCard label="T.Withdraw" value={fmt(tTotals.tWithdraw)} color="#e74c3c" th={th} />
                  <StatCard label="T.Volume" value={fmtN(tTotals.tVolume)} color="#8b5cf6" th={th} />
                  <StatCard label="T.Profit" value={fmt(tTotals.tProfit)} color={tTotals.tProfit >= 0 ? '#16835e' : '#e74c3c'} th={th} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  {/* New Logins + New Clients: sourced from myAcc2026 (2026+ accounts data), filtered by selected month/year */}
                  {(function() {
                    var monthNum = { January:'01', February:'02', March:'03', April:'04', May:'05', June:'06', July:'07', August:'08', September:'09', October:'10', November:'11', December:'12' };
                    var selMs = pMonth.length ? pMonth.map(function(m) { return monthNum[m] || ''; }).filter(Boolean) : [];
                    var selYs = pYear.length ? pYear.slice() : [];
                    var isAll = !selMs.length && !selYs.length;
                    var accSource = myAcc2026;
                    if (fAgent.length) {
                      accSource = accSource.filter(function(a) { return fAgent.indexOf(a.agent) >= 0; });
                    }
                    if (!isAll) {
                      accSource = accSource.filter(function(a) {
                        var d = (a.registration || a.date || '').slice(0, 7);
                        if (!d) return false;
                        var dY = d.slice(0, 4);
                        var dM = d.slice(5, 7);
                        if (selMs.length && selYs.length) {
                          return selYs.indexOf(dY) >= 0 && selMs.indexOf(dM) >= 0;
                        } else if (selYs.length) {
                          return selYs.indexOf(dY) >= 0;
                        } else if (selMs.length) {
                          return selMs.indexOf(dM) >= 0;
                        }
                        return true;
                      });
                    }
                    var totalLogins = accSource.length;
                    var clientCUs = new Set();
                    var details = [];
                    accSource.forEach(function(a) {
                      if (!a.id) return;
                      if (a.clientStatus === 'New Client' && !clientCUs.has(a.id)) {
                        clientCUs.add(a.id);
                        details.push({ cu: a.id, login: a.login || '', name: a.client || '', crmOpenDate: a.date || '', agent: a.agent || '' });
                      }
                    });
                    var period = isAll ? 'All' : (selMs.length && selYs.length) ? selYs.join(',') + '-' + selMs.join(',') : selYs.length ? selYs.join(',') : 'Month ' + selMs.join(',');
                    return <React.Fragment>
                      <div style={{ cursor: 'pointer' }} title="Click to see all logins" onClick={function() {
                        var msg = 'New Logins in ' + period + ': ' + totalLogins + '\n\n';
                        accSource.slice().sort(function(a, b) { return (b.date || '').localeCompare(a.date || ''); }).forEach(function(d, i) { msg += (i + 1) + '. CU: ' + d.id + ' | Login: ' + d.login + ' | ' + (d.client || '—') + ' | Opened: ' + d.date + ' | Agent: ' + (d.agent || '—') + '\n'; });
                        alert(msg);
                      }}><StatCard label={ll.newLogins} value={fmtN(totalLogins)} color="#0ea5e9" th={th} /></div>
                      <div style={{ cursor: 'pointer' }} title="Click to see new clients" onClick={function() {
                        var msg = 'New Clients in ' + period + ': ' + details.length + '\n\n';
                        details.sort(function(a, b) { return (b.crmOpenDate || '').localeCompare(a.crmOpenDate || ''); });
                        details.forEach(function(d, i) { msg += (i + 1) + '. CU: ' + d.cu + ' | Login: ' + d.login + ' | ' + (d.name || '—') + ' | Opened: ' + d.crmOpenDate + ' | Agent: ' + (d.agent || '—') + '\n'; });
                        alert(msg);
                      }}><StatCard label={ll.newClients} value={fmtN(clientCUs.size)} color="#8b5cf6" th={th} /></div>
                    </React.Fragment>;
                  })()}
                  {/* Retention: filtered by selected month/year */}
                  {(function() {
                    var retFiltered = myRetention;
                    if (fAgent.length) {
                      retFiltered = retFiltered.filter(function(x) { return fAgent.indexOf(x.agent) >= 0; });
                    }
                    if (pMonth.length || pYear.length) {
                      var monthNum = { January:'01', February:'02', March:'03', April:'04', May:'05', June:'06', July:'07', August:'08', September:'09', October:'10', November:'11', December:'12' };
                      var selMs = pMonth.length ? pMonth.map(function(m) { return monthNum[m] || ''; }).filter(Boolean) : [];
                      var selYs = pYear.length ? pYear.slice() : [];
                      retFiltered = retFiltered.filter(function(r) {
                        var mk = String(r.monthKey || '');
                        var rY = mk.slice(0, 4);
                        var rM = mk.slice(4, 6);
                        if (selYs.length && selYs.indexOf(rY) < 0) return false;
                        if (selMs.length && selMs.indexOf(rM) < 0) return false;
                        return true;
                      });
                    }
                    return <StatCard label="🔁 Retention" value={fmtN(retFiltered.length)} color="#0ea5e9" th={th} />;
                  })()}
                  <StatCard label="Deposit" value={fmt(ps.deposit)} color="#16835e" th={th} />
                  <StatCard label="Withdraw" value={fmt(ps.withdraw)} color="#e74c3c" th={th} />
                  <StatCard label="Volume" value={fmtN(ps.volume)} color="#8b5cf6" th={th} />
                  <StatCard label="Profit" value={fmt(ps.profit)} color={ps.profit >= 0 ? '#16835e' : '#e74c3c'} th={th} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <StatCard label="Equity" value={fmt(ps.equity)} color={th.accent} th={th} />
                  <StatCard label="Floating P/L" value={fmt(ps.floatingPL)} color={ps.floatingPL >= 0 ? '#16835e' : '#e74c3c'} th={th} />
                  <StatCard label="Equity Prev" value={fmt(ps.equityPrevMonth)} color="#6366f1" th={th} />
                  <StatCard label="Total Trades" value={ps.totalTrades} color="#2b6cb0" th={th} />
                  <StatCard label="Total Deals" value={ps.totalDeals} color="#8b5cf6" th={th} />
                  {(function() {
                    var filteredLogins = new Set(pFiltered.map(function(r) { return String(r.login || ''); }));
                    var monthNum = { January:'01', February:'02', March:'03', April:'04', May:'05', June:'06', July:'07', August:'08', September:'09', October:'10', November:'11', December:'12' };
                    var selMs = pMonth.length ? pMonth.map(function(m) { return monthNum[m] || ''; }).filter(Boolean) : [];
                    var selYs = pYear.length ? pYear.slice() : [];
                    var ftdTotal = myFtd.reduce(function(s, f) {
                      if (!filteredLogins.has(String(f.login || ''))) return s;
                      var fd = f.ftdDate || '';
                      if (selYs.length && selYs.indexOf(fd.slice(0, 4)) < 0) return s;
                      if (selMs.length && selMs.indexOf(fd.slice(5, 7)) < 0) return s;
                      return s + (Number(f.ftdAmount) || 0);
                    }, 0);
                    return <StatCard label="FTD" value={fmt(ftdTotal)} color="#f59e0b" th={th} />;
                  })()}
                  <StatCard label="Last Access" value={ps.lastAccess || '-'} color={th.sub} th={th} />
                </div>
                <SortableTable th={th} rtl={rtl} empty={ll.noData} data={pFiltered} cols={[
                  { key: 'cu', label: 'CU Account', render: function(r) {
                    var hasFu = !!fuByLogin[r.login];
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {(role === 'agent' || role === 'admin') && (
                          <button onClick={function() {
                            var existing = fuByLogin[r.login];
                            setFuModal(existing ? Object.assign({}, existing) : { cu: r.cu, login: String(r.login), name: r.name, agent: r.agent || (userProfile ? userProfile.name : ''), status: '', action: '', comment: '', _id: null });
                          }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}
                            title={hasFu ? 'Edit follow-up' : 'Add follow-up'}>{hasFu ? '📝' : '➕'}</button>
                        )}
                        <CopyCell value={r.cu} color={th.accent} />
                        {hasFu && <span style={{ fontSize: 11 }} title={fuByLogin[r.login].comment || ''}>💬</span>}
                      </div>
                    );
                  }},
                  { key: 'login', label: 'Login', style: { fontSize: 11, fontWeight: 600 } },
                  { key: 'name', label: 'Client Name', style: { fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                  { key: 'phone', label: 'Mobile', render: function(r) {
                    return <span style={{ fontSize: 11, direction: 'ltr' }}>{r.phone || '—'}</span>;
                  } },
                  { key: 'country', label: 'Country', style: { fontSize: 10 } },
                  { key: 'tDeposit', label: 'T.Deposit', render: function(r) { return <span style={{ color: '#16835e', fontWeight: 600 }}>{fmt(r.tDeposit)}</span>; } },
                  { key: 'tWithdraw', label: 'T.Withdraw', render: function(r) { return <span style={{ color: '#e74c3c' }}>{fmt(r.tWithdraw)}</span>; } },
                  { key: 'tVolume', label: 'T.Volume', render: function(r) { return fmtN(r.tVolume); } },
                  { key: 'tProfit', label: 'T.Profit', render: function(r) { var v = r.tProfit; return <span style={{ color: v >= 0 ? '#16835e' : '#e74c3c', fontWeight: 600 }}>{fmt(v)}</span>; } },
                  { key: 'deposit', label: 'Deposit', render: function(r) { return <span style={{ color: '#16835e' }}>{fmt(r.deposit)}</span>; } },
                  { key: 'withdraw', label: 'Withdraw', render: function(r) { return <span style={{ color: '#e74c3c' }}>{fmt(r.withdraw)}</span>; } },
                  { key: 'volume', label: 'Volume', render: function(r) { return fmtN(r.volume); } },
                  { key: 'profit', label: 'Profit', render: function(r) { var v = r.profit; return <span style={{ color: v >= 0 ? '#16835e' : '#e74c3c' }}>{fmt(v)}</span>; } },
                  { key: 'firstDeposit', label: 'FTD', render: function(r) { var v = Number(r.firstDeposit) || 0; return <span style={{ fontWeight: 600, color: v > 0 ? '#16835e' : '#e74c3c' }}>{'$' + v.toLocaleString()}</span>; } },
                  { key: 'equity', label: 'Equity', render: function(r) { return <span style={{ fontWeight: 600 }}>{fmt(r.equity)}</span>; } },
                  { key: 'floatingPL', label: 'Float P/L', render: function(r) { var v = r.floatingPL; return <span style={{ color: v >= 0 ? '#16835e' : '#e74c3c' }}>{fmt(v)}</span>; } },
                  { key: 'equityPrevMonth', label: 'Eq.Prev', render: function(r) { return fmt(r.equityPrevMonth); } },
                  { key: 'totalTrades', label: 'Total Trades', render: function(r) { return (r.totalTrades || 0).toLocaleString(); } },
                  { key: 'totalDeals', label: 'Total Deals', render: function(r) { return (r.totalDeals || 0).toLocaleString(); } },
                  { key: 'lastAccess', label: 'Last Access', style: { fontSize: 10, color: th.sub } },
                  { key: 'registration', label: 'Registration', style: { fontSize: 10, color: th.sub } },
                  { key: 'crmOpenDate', label: 'CRM Open', style: { fontSize: 10, color: th.sub } },
                  { key: 'clientStatus', label: 'Status', render: function(r) { return <StatusBadge value={r.clientStatus} colorMap={CLIENT_STATUS_COLORS} />; } },
                  { key: 'accountType', label: 'Acc.Type', style: { fontSize: 10, whiteSpace: 'nowrap' } },
                  { key: 'clientType', label: 'Client Type', style: { fontSize: 10, whiteSpace: 'nowrap' } },
                  { key: 'mostInstrument', label: 'Instrument', style: { fontSize: 10 } },
                  { key: 'mostTradedInstruments', label: 'Most Traded', style: { fontSize: 10 } },
                ].concat(role !== 'agent' ? [{ key: 'agent', label: 'Agent', style: { fontSize: 11 } }] : [])} />
              </div>
            );
          })()}

          {/* ═══════ ACCOUNTS ═══════ */}
          {tab === 'accounts' && (
            <div>
              {accountsHook.loading ? <Spinner th={th} text={ll.loading} /> : (
                <div>
                  {renderFilters({ showStatus: false, extraFilters: <React.Fragment>
                    <div style={{ minWidth: 130 }}><MultiDropdown value={fAccCountry} options={Array.from(new Set(myAcc2026.map(function(a) { return a.country; }).filter(Boolean))).sort()} onChange={function(v) { setFAccCountry(v); }} placeholder="All Countries" th={th} /></div>
                    <div style={{ minWidth: 130 }}><MultiDropdown value={fAccClientStatus} options={Array.from(new Set(myAcc2026.map(function(a) { return a.clientStatus; }).filter(Boolean))).sort()} onChange={function(v) { setFAccClientStatus(v); }} placeholder="All Status" th={th} /></div>
                  </React.Fragment> })}
                  {(function() {
                    // Build leads phone lookup (last 9 digits → lead info)
                    var leadsPhoneMap = {};
                    leadsHook.data.forEach(function(l) {
                      if (l.phone) {
                        var digits = String(l.phone).replace(/\D/g, '');
                        var last9 = digits.slice(-9);
                        if (last9.length >= 9) leadsPhoneMap[last9] = l;
                      }
                    });
                    var getSource = function(phone) {
                      if (!phone) return null;
                      var digits = String(phone).replace(/\D/g, '');
                      var last9 = digits.slice(-9);
                      if (last9.length >= 9 && leadsPhoneMap[last9]) return leadsPhoneMap[last9];
                      return null;
                    };

                    var accFiltered = filterSearch(filterDate(fAgent.length ? myAcc2026.filter(function(x) { return fAgent.indexOf(x.agent) >= 0; }) : myAcc2026, 'registration'));
                    // Apply Country filter
                    if (fAccCountry.length) {
                      accFiltered = accFiltered.filter(function(x) { return fAccCountry.indexOf(x.country) >= 0; });
                    }
                    // Apply ClientStatus filter
                    if (fAccClientStatus.length) {
                      accFiltered = accFiltered.filter(function(x) { return fAccClientStatus.indexOf(x.clientStatus) >= 0; });
                    }
                    // Sort by registration date descending then open date descending (newest first)
                    accFiltered = accFiltered.slice().sort(function(a, b) { return (b.registration || b.date || '').localeCompare(a.registration || a.date || ''); });

                    // Count New Logins (total rows) and New Clients (unique CU with New Client status)
                    var totalLogins = accFiltered.length;
                    var clientCUs = new Set();
                    accFiltered.forEach(function(r) {
                      if (r.clientStatus === 'New Client' && r.id) clientCUs.add(r.id);
                    });
                    var totalNewClients = clientCUs.size;

                    // Source counting based on unique CU
                    var cuSeen = new Set();
                    var uniqueAccFiltered = [];
                    accFiltered.forEach(function(r) {
                      if (!cuSeen.has(r.id)) { cuSeen.add(r.id); uniqueAccFiltered.push(r); }
                    });
                    var leadCount = 0;
                    var directCount = 0;
                    var sourceMap = {};
                    uniqueAccFiltered.forEach(function(r) {
                      var lead = getSource(r.phone);
                      if (lead && lead.uploadedBy) {
                        leadCount++;
                        var src = normalizeLeadSource(lead.uploadedBy);
                        sourceMap[src] = (sourceMap[src] || 0) + 1;
                      } else {
                        directCount++;
                      }
                    });
                    var sourceColors = ['#16835e', '#2b6cb0', '#8b5cf6', '#f59e0b', '#e74c3c', '#0ea5e9', '#f97316'];
                    return (
                      <div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                          <div style={{ background: '#dbeafe', borderRadius: 8, padding: '8px 14px', border: '1px solid #93c5fd', fontSize: 12, fontWeight: 700, color: '#2b6cb0' }}>{ll.newLogins + ': ' + totalLogins}</div>
                          <div style={{ background: '#d1fae5', borderRadius: 8, padding: '8px 14px', border: '1px solid #6ee7b7', fontSize: 12, fontWeight: 700, color: '#16835e' }}>{ll.newClients + ': ' + totalNewClients}</div>
                          {Object.keys(sourceMap).map(function(src, i) {
                            var c = sourceColors[i % sourceColors.length];
                            return <div key={src} style={{ background: c + '18', borderRadius: 8, padding: '8px 14px', border: '1px solid ' + c + '44', fontSize: 12, fontWeight: 700, color: c }}>{src + ': ' + sourceMap[src]}</div>;
                          })}
                          {directCount > 0 && <div style={{ background: '#e8e0f4', borderRadius: 8, padding: '8px 14px', border: '1px solid #d4c9e8', fontSize: 12, fontWeight: 700, color: '#8b5cf6' }}>{'Direct: ' + directCount}</div>}
                        </div>
                        <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[
                          { key: 'id', label: ll.cuAccount, render: function(r) { return <CopyCell value={r.id} color={th.accent} />; } },
                          { key: 'login', label: 'Login', style: { fontSize: 11 } },
                          { key: 'client', label: ll.clientName, style: { fontWeight: 600 } },
                          { key: 'phone', label: ll.phone, style: { direction: 'ltr', fontSize: 11 } },
                        ].concat(role !== 'agent' ? [{ key: 'agent', label: ll.accountManager, style: { fontSize: 11 } }] : []).concat([
                          { key: 'country', label: 'Country', style: { fontSize: 11 } },
                          { key: 'date', label: ll.openDate, style: { color: th.sub, fontSize: 11 } },
                          { key: 'registration', label: 'Registration', style: { color: th.sub, fontSize: 11 } },
                          { key: 'clientStatus', label: 'Client Status', render: function(r) {
                            if (r.clientStatus === 'New Client') return <span style={{ background: '#d1fae5', color: '#065f46', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>New Client</span>;
                            if (r.clientStatus === 'Old Client') return <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>Old Client</span>;
                            return <span style={{ fontSize: 11 }}>{r.clientStatus || ''}</span>;
                          }},
                          { key: 'loginStatus', label: 'Login Status', render: function(r) {
                            if (r.loginStatus) return <span style={{ background: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>{r.loginStatus}</span>;
                            return '';
                          }},
                          { key: '_source', label: 'Source', render: function(r) {
                            var lead = getSource(r.phone);
                            if (lead && lead.uploadedBy) {
                              return <span style={{ background: '#d4edda', color: '#16835e', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>{normalizeLeadSource(lead.uploadedBy)}</span>;
                            }
                            return <span style={{ background: '#e8e0f4', color: '#8b5cf6', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>Direct</span>;
                          }},
                        ])} data={accFiltered} />
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ═══════ FTD ═══════ */}
          {tab === 'ftd' && (function() {
            if (ftdHook.loading) return <Spinner th={th} text={ll.loading} />;
            var monthNum = { January:'01', February:'02', March:'03', April:'04', May:'05', June:'06', July:'07', August:'08', September:'09', October:'10', November:'11', December:'12' };
            var monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            // Build available years from FTD data
            var ftdYears = new Set();
            myFtd.forEach(function(r) { var y = (r.ftdDate || '').slice(0, 4); if (y && y.length === 4) ftdYears.add(y); });
            var yearOpts = Array.from(ftdYears).sort().reverse();

            // Deduplicate: keep only latest record per login
            var rawFtd = fAgent.length ? myFtd.filter(function(x) { return fAgent.indexOf(x.agent) >= 0; }) : myFtd;
            var ftdGroups = {};
            rawFtd.forEach(function(r) {
              var key = r.login || r.id || r.client || Math.random();
              if (!ftdGroups[key] || (r.ftdDate || '') > (ftdGroups[key].ftdDate || '')) ftdGroups[key] = r;
            });
            var dedupedFtd = Object.values(ftdGroups);

            // Filter by month/year
            var selMs = ftdMonth.length ? ftdMonth.map(function(m) { return monthNum[m] || ''; }).filter(Boolean) : [];
            var selYs = ftdYear.length ? ftdYear.slice() : [];
            var d = dedupedFtd.filter(function(r) {
              var fd = r.ftdDate || '';
              if (selYs.length && selYs.indexOf(fd.slice(0, 4)) < 0) return false;
              if (selMs.length && selMs.indexOf(fd.slice(5, 7)) < 0) return false;
              return true;
            });
            d = filterSearch(d);
            if (fFtdType.length) d = d.filter(function(x) { return fFtdType.indexOf(x.clientStatus) >= 0; });
            d.sort(function(a, b) { return (b.ftdDate || '').localeCompare(a.ftdDate || ''); });
            var ftdTot = d.reduce(function(s, x) { return s + (Number(x.ftdAmount) || 0); }, 0);
            var ftdWithAmount = d.filter(function(x) { return (Number(x.ftdAmount) || 0) > 0; }).length;

            var ftdLoginSet = new Set();
            myFtd.forEach(function(f) { if (String(f.login || '')) ftdLoginSet.add(String(f.login)); });
            var nfData = fAgent.length ? myPort.filter(function(p) { return fAgent.indexOf(p.agent) >= 0; }) : myPort;
            var nfGroups = {};
            nfData.forEach(function(p) {
              var key = String(p.login || '');
              if (!key || ftdLoginSet.has(key)) return;
              if (!nfGroups[key] || (Number(p.monthKey) || 0) > (Number(nfGroups[key].monthKey) || 0)) nfGroups[key] = p;
            });
            var nonFundedList = Object.values(nfGroups).map(function(p) {
              return { cu: p.cu, login: String(p.login), name: (p.name || '').trim().replace(/\s+/g, ' '), registration: p.registration || '', agent: p.agent || '' };
            });
            nonFundedList = nonFundedList.filter(function(r) {
              if (!search) return true;
              var s = search.toLowerCase();
              return (r.cu && r.cu.toLowerCase().indexOf(s) >= 0) || (r.name && r.name.toLowerCase().indexOf(s) >= 0) || (String(r.login).indexOf(s) >= 0) || (r.agent && r.agent.toLowerCase().indexOf(s) >= 0);
            });
            nonFundedList.sort(function(a, b) { return (a.name || '').localeCompare(b.name || ''); });
            var nonFundedCount = nonFundedList.length;
            var toggleBtn = function(label, active) {
              return { padding: '7px 16px', borderRadius: 6, border: active ? '2px solid ' + th.accent : '1px solid ' + th.border, background: active ? th.accent : th.card, color: active ? '#fff' : th.sub, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
            };
            return (
              <div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  <button onClick={function() { setFtdViewMode('ftd'); }} style={toggleBtn('FTD', ftdViewMode === 'ftd')}>FTD</button>
                  <button onClick={function() { setFtdViewMode('nonfunded'); }} style={toggleBtn('Non Funded', ftdViewMode === 'nonfunded')}>Non Funded</button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <input placeholder={ll.search} value={search} onChange={function(e) { setSearch(e.target.value); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid ' + th.inpB, borderRadius: 8, background: th.inp, fontSize: 13, color: th.text }} />
                  </div>
                  {role !== 'agent' && (
                    <div style={{ minWidth: 140 }}>
                      <MultiDropdown value={fAgent} options={allAgentNamesFromData} onChange={function(v) { setFAgent(v); }} placeholder={ll.allAgents} th={th} />
                    </div>
                  )}
                  <div style={{ minWidth: 120 }}>
                    {ftdViewMode === 'ftd' && <MultiDropdown value={fFtdType} options={['New Client', 'Old Client']}
                      onChange={function(v) { setFFtdType(v); }}
                      placeholder="All Types" th={th} />}
                  </div>
                  {ftdViewMode === 'ftd' && <div style={{ minWidth: 120 }}>
                    <MultiDropdown value={ftdMonth} options={monthNames}
                      onChange={function(v) { setFtdMonth(v); }}
                      placeholder="All Months" th={th} />
                  </div>}
                  {ftdViewMode === 'ftd' && <div style={{ minWidth: 90 }}>
                    <MultiDropdown value={ftdYear} options={yearOpts}
                      onChange={function(v) { setFtdYear(v); }}
                      placeholder="All Years" th={th} />
                  </div>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#16835e' }}>{'Total: $' + ftdTot.toLocaleString()}</div>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#e74c3c' }}>{'Non Funded: ' + nonFundedCount}</div>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#2b6cb0' }}>{'FTD Count: ' + ftdWithAmount}</div>
                </div>
                {ftdViewMode === 'ftd' ? (
                <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[
                  { key: 'id', label: ll.cuAccount, render: function(r) { return <CopyCell value={r.id} color={th.accent} />; } },
                  { key: 'login', label: 'Login', style: { fontSize: 11 } },
                  { key: 'client', label: ll.clientName, style: { fontWeight: 600 } },
                  { key: 'regDate', label: 'Reg. Date', style: { fontSize: 10, color: th.sub } },
                  { key: 'ftdAmount', label: ll.amount, render: function(r) { return <span style={{ fontWeight: 700, color: '#16835e' }}>{'$' + (Number(r.ftdAmount) || 0).toLocaleString()}</span>; } },
                  { key: 'ftdDate', label: ll.ftdDate, style: { fontSize: 11 } },
                  { key: 'ftdTime', label: 'Time', style: { fontSize: 10, color: th.sub } },
                  { key: 'clientStatus', label: ll.clientStatus, render: function(r) { return <TypeBadge type={r.clientStatus} />; } },
                ].concat(role !== 'agent' ? [{ key: 'agent', label: ll.accountManager, style: { fontSize: 11 } }] : [])} data={d} />
                ) : (
                <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[
                  { key: 'cu', label: ll.cuAccount, render: function(r) { return <CopyCell value={r.cu} color={th.accent} />; } },
                  { key: 'login', label: 'Login', style: { fontSize: 11 } },
                  { key: 'name', label: ll.clientName, style: { fontWeight: 600 } },
                  { key: 'registration', label: 'Reg. Date', style: { fontSize: 10, color: th.sub } },
                ].concat(role !== 'agent' ? [{ key: 'agent', label: ll.accountManager, style: { fontSize: 11 } }] : [])} data={nonFundedList} />
                )}
              </div>
            );
          })()}

          {/* ═══════ LEADS (Manager/Admin) ═══════ */}
          {tab === 'leads' && role !== 'agent' && (function() {
            if (leadsHook.loading) return <Spinner th={th} text={ll.loading} />;
            var leadsFiltered = filterSearch(filterDate(fAgent.length ? myLeadsDeduped.filter(function(x) { return fAgent.indexOf(x.agent) >= 0; }) : myLeadsDeduped, 'date')).filter(function(x) { return !fStatus.length || (fStatus.indexOf('— Empty —') >= 0 && !x.status) || (x.status && fStatus.indexOf(x.status) >= 0); }).filter(function(x) { return !fAction.length || (fAction.indexOf('— Empty —') >= 0 && !x.action) || (x.action && fAction.indexOf(x.action) >= 0); });
            var selCount = Object.keys(selectedLeads).filter(function(k) { return selectedLeads[k]; }).length;
            var allSelected = leadsFiltered.length > 0 && leadsFiltered.every(function(r) { return selectedLeads[r._id]; });

            var toggleAll = function() {
              var next = {};
              if (!allSelected) {
                leadsFiltered.forEach(function(r) { next[r._id] = true; });
              }
              setSelectedLeads(next);
            };

            var toggleOne = function(id) {
              var next = Object.assign({}, selectedLeads);
              next[id] = !next[id];
              if (!next[id]) delete next[id];
              setSelectedLeads(next);
            };

            var deleteSelected = function() {
              var ids = Object.keys(selectedLeads).filter(function(k) { return selectedLeads[k]; });
              if (ids.length === 0) return;
              if (!window.confirm('Delete ' + ids.length + ' selected leads?')) return;
              var promises = ids.map(function(id) { return leadsHook.deleteItem(id); });
              Promise.all(promises).then(function() { showMsg(ids.length + ' leads deleted'); setSelectedLeads({}); });
            };

            return (
              <div>
                {renderFilters({ showStatus: true, showAction: true })}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={function() { setShowAddLead(true); }} style={{ background: th.accent, color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    {'+ ' + ll.addNewLead}
                  </button>
                  {selCount > 0 && role === 'admin' && (
                    <button onClick={deleteSelected} style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      {'🗑 Delete Selected (' + selCount + ')'}
                    </button>
                  )}
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#e74c3c' }}>{'Total: ' + leadsFiltered.length}</div>
                </div>
                {selCount > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', background: th.card, borderRadius: 8, padding: '8px 14px', border: '2px solid ' + th.accent, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: th.accent }}>{selCount + ' selected'}</span>
                    <span style={{ fontSize: 11, color: th.sub }}>Reassign to:</span>
                    <select id="bulkReassignAgent" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid ' + th.border, background: th.inp, color: th.inpT, fontSize: 11 }}>
                      <option value="">Select Agent...</option>
                      {allAgentNamesFromData.map(function(a) { return <option key={a} value={a}>{a}</option>; })}
                    </select>
                    <button onClick={function() {
                      var agent = document.getElementById('bulkReassignAgent').value;
                      if (!agent) { showMsg('Select an agent first'); return; }
                      var ids = Object.keys(selectedLeads).filter(function(k) { return selectedLeads[k]; });
                      var promises = ids.map(function(id) { return leadsHook.updateItem(id, { agent: agent, date: localNow().slice(0, 10) }); });
                      Promise.all(promises).then(function() { showMsg(ids.length + ' leads reassigned to ' + agent); setSelectedLeads({}); });
                    }} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#16835e', color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      Apply
                    </button>
                  </div>
                )}
                <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[].concat((role === 'admin' || role === 'manager') ? [{ key: '_check', label: '', sortable: false, style: { width: 30, textAlign: 'center' },
                  headerRender: function() { return <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor: 'pointer' }} />; },
                  render: function(r) { return <input type="checkbox" checked={!!selectedLeads[r._id]} onChange={function() { toggleOne(r._id); }} style={{ cursor: 'pointer' }} />; }
                }] : []).concat([
                  { key: 'client', label: ll.clientName, style: { fontWeight: 600 } },
                  { key: 'phone', label: ll.phone, style: { direction: 'ltr', fontSize: 11 }, render: function(r) { return <CopyCell value={r.phone || ''} />; } },
                  { key: 'agent', label: ll.accountManager, style: { fontSize: 11 } },
                  { key: 'date', label: ll.date, style: { color: th.sub, fontSize: 11 } },
                  { key: 'status', label: ll.status, render: function(r) { return <StatusBadge value={r.status} />; } },
                  { key: 'action', label: ll.action, render: function(r) { return <ActionBadge value={r.action} />; } },
                  { key: 'comments', label: ll.comments, render: function(r) {
                    if (!r.comments) return <span style={{ color: '#aaa', fontSize: 10 }}>—</span>;
                    return <div onClick={function() { setCommentPopup(r.comments); }} style={{ maxWidth: 180, fontSize: 11, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '1.4', cursor: 'pointer', color: '#2b6cb0', textDecoration: 'underline dotted' }}>{r.comments}</div>;
                  } },
                  { key: 'uploadedBy', label: ll.uploadedBy, style: { fontSize: 10, color: th.sub }, render: function(r) { return normalizeLeadSource(r.uploadedBy || ''); } },
                ]).concat(role === 'admin' ? [{ key: '_actions', label: 'Actions', sortable: false, render: function(r) {
                  return (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={function() { setEditLead(Object.assign({}, r)); }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid ' + th.border, background: '#2b6cb0', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                      <button onClick={function() { if (window.confirm('Delete lead "' + r.client + '"?')) { leadsHook.deleteItem(r._id); showMsg('Deleted'); } }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Del</button>
                    </div>
                  );
                }}] : role === 'manager' ? [{ key: '_actions', label: 'Assign', sortable: false, render: function(r) {
                  return (
                    <button onClick={function() { setEditLead(Object.assign({}, r)); }}
                      style={{ padding: '3px 10px', borderRadius: 4, border: 'none', background: r.agent ? '#f59e0b' : '#16835e', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>{r.agent ? '✏ Reassign' : '👤 Assign'}</button>
                  );
                }}] : [])} data={leadsFiltered} rowClassName={function(r) { return r._id === highlightId ? 'highlight-row' : ''; }} />
              </div>
            );
          })()}

          {/* ═══════ LEADS (Agent - card view) ═══════ */}
          {tab === 'leads' && role === 'agent' && (function() {
            if (leadsHook.loading) return <Spinner th={th} text={ll.loading} />;
            var agentLeads = myLeadsDeduped.filter(function(x) {
              if (fStatus.length && !(fStatus.indexOf('— Empty —') >= 0 && !x.status) && !(x.status && fStatus.indexOf(x.status) >= 0)) return false;
              if (fAction.length && !(fAction.indexOf('— Empty —') >= 0 && !x.action) && !(x.action && fAction.indexOf(x.action) >= 0)) return false;
              return true;
            });
            agentLeads = filterSearch(filterDate(agentLeads, 'date'));
            return (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder={ll.search}
                  style={{ flex: '1 1 180px', padding: '8px 10px', borderRadius: 8, border: '1px solid ' + th.border, background: th.card, color: th.text, fontSize: 12, outline: 'none' }} />
                <div style={{ minWidth: 130 }}>
                  <MultiDropdown value={fStatus} options={['— Empty —'].concat(STATUSES)}
                    onChange={function(v) { setFStatus(v); }}
                    placeholder={ll.allStatuses || 'All Statuses'} th={th} />
                </div>
                <div style={{ minWidth: 130 }}>
                  <MultiDropdown value={fAction} options={['— Empty —'].concat(ACTIONS)}
                    onChange={function(v) { setFAction(v); }}
                    placeholder={ll.allActions || 'All Actions'} th={th} />
                </div>
                <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + th.border, background: th.card, color: th.text, fontSize: 11 }} />
                <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + th.border, background: th.card, color: th.text, fontSize: 11 }} />
                {(search || fStatus.length || fAction.length || dateFrom || dateTo) && (
                  <button onClick={function() { setSearch(''); setFStatus([]); setFAction([]); setDateFrom(''); setDateTo(''); }}
                    style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#e74c3c', color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Clear</button>
                )}
                <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#e74c3c' }}>{'Total: ' + agentLeads.length}</div>
              </div>
              {agentLeads.length === 0 && <div style={{ background: th.card, borderRadius: 10, padding: 28, textAlign: 'center', color: th.sub, border: '1px solid ' + th.border }}>{ll.noData}</div>}
              {agentLeads.map(function(ld) {
                    var selectStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, color: th.inpT, outline: 'none', cursor: 'pointer', appearance: 'auto' };
                    var isEditing = editingLeadId === ld._id;
                    return (
                      <div key={ld._id} className={ld._id === highlightId ? 'highlight-row' : ''} style={{ background: th.card, borderRadius: 12, border: isEditing ? '2px solid ' + th.accent : '1px solid ' + th.border, marginBottom: 10 }}>
                        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid ' + th.border, flexWrap: 'wrap', gap: 6 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: th.text, marginBottom: 2 }}>{ld.client}</div>
                            <div style={{ fontSize: 11, color: th.sub, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <CopyCell value={ld.phone || ''} />
                              <span>{'  |  ' + (ld.date || '')}</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <StatusBadge value={ld.status} />
                            {!isEditing && (
                              <button onClick={function() { setEditingLeadId(ld._id); setEditingLeadData({ client: ld.client || '', status: ld.status || '', action: ld.action || '', comments: ld.comments || '', reminderDate: ld.reminderDate || '', reminderTime: ld.reminderTime || '' }); }}
                                style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid ' + th.border, background: '#2b6cb0', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>✏ Edit</button>
                            )}
                            {role === 'admin' && (
                              <button onClick={function() { if (window.confirm('Delete lead "' + ld.client + '"?')) { leadsHook.deleteItem(ld._id); showMsg('Deleted'); } }}
                                style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>🗑</button>
                            )}
                          </div>
                        </div>
                        {isEditing ? (
                          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                              <label style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 4, display: 'block' }}>{ll.clientName}</label>
                              <input value={editingLeadData.client || ''} onChange={function(e) { setEditingLeadData(Object.assign({}, editingLeadData, { client: e.target.value })); }}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              <div style={{ flex: '1 1 160px' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 4, display: 'block' }}>{ll.status}</label>
                                <select value={editingLeadData.status} onChange={function(e) { setEditingLeadData(Object.assign({}, editingLeadData, { status: e.target.value })); }} style={selectStyle}>
                                  <option value="">{ll.selectStatus}</option>
                                  {STATUSES.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
                                </select>
                              </div>
                              <div style={{ flex: '1 1 160px' }}>
                                <label style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 4, display: 'block' }}>{ll.action}</label>
                                <select value={editingLeadData.action} onChange={function(e) { setEditingLeadData(Object.assign({}, editingLeadData, { action: e.target.value })); }} style={selectStyle}>
                                  <option value="">{ll.selectAction}</option>
                                  {ACTIONS.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 4, display: 'block' }}>{ll.comments}</label>
                              <textarea value={editingLeadData.comments}
                                onChange={function(e) { if (e.target.value.length <= 200) setEditingLeadData(Object.assign({}, editingLeadData, { comments: e.target.value })); }}
                                placeholder={ll.writeComment} maxLength={200}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, fontSize: 12, outline: 'none', resize: 'vertical', minHeight: 60, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fffbeb', color: th.inpT }} />
                              <div style={{ fontSize: 9, color: editingLeadData.comments.length >= 180 ? '#e74c3c' : th.sub, textAlign: 'right', marginTop: 2 }}>{editingLeadData.comments.length + ' / 200'}</div>
                            </div>
                            <div>
                              <label style={{ fontSize: 10, fontWeight: 600, color: th.sub, marginBottom: 4, display: 'block' }}>🔔 Reminder</label>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <input type="date" value={editingLeadData.reminderDate || ''} onChange={function(e) { setEditingLeadData(Object.assign({}, editingLeadData, { reminderDate: e.target.value })); }}
                                  style={{ flex: '1 1 130px', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                                <input type="time" value={editingLeadData.reminderTime || ''} onChange={function(e) { setEditingLeadData(Object.assign({}, editingLeadData, { reminderTime: e.target.value })); }}
                                  style={{ flex: '1 1 100px', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                                {(editingLeadData.reminderDate || editingLeadData.reminderTime) && (
                                  <button onClick={function() { setEditingLeadData(Object.assign({}, editingLeadData, { reminderDate: '', reminderTime: '' })); }}
                                    style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Clear</button>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={function() { setEditingLeadId(null); setEditingLeadData({}); }}
                                style={{ flex: 1, padding: '8px 14px', borderRadius: 6, border: '1px solid ' + th.border, background: th.card, color: th.sub, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{ll.cancel}</button>
                              <button onClick={function() { delete firedRemindersRef.current['lead_' + ld._id]; leadsHook.updateItem(ld._id, editingLeadData).then(function() { showMsg(ll.saved); setEditingLeadId(null); setEditingLeadData({}); }); }}
                                style={{ flex: 1, padding: '8px 14px', borderRadius: 6, border: 'none', background: '#16835e', color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>💾 Save</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: ld.comments ? 8 : 0, alignItems: 'center' }}>
                              {ld.action && <ActionBadge value={ld.action} />}
                            </div>
                            {ld.comments && <div style={{ fontSize: 11, color: th.text, background: '#fffbeb', padding: '6px 10px', borderRadius: 6, border: '1px solid ' + th.border, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{ld.comments}</div>}
                            {ld.reminderDate && ld.reminderTime && <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 6, fontWeight: 600 }}>{'🔔 Reminder: ' + ld.reminderDate + ' ' + ld.reminderTime}</div>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
          })()}

          {/* ═══════ FOLLOW UP ═══════ */}
          {tab === 'notactive' && (function() {
            if (portfolioLoading) return <Spinner th={th} text={ll.loading} />;

            // Get portfolio records that are not active (clientStatus === 'Not Active')
            // Uses portAggregatedNotActive which is independent of Portfolio tab filters
            var sourceRows = portAggregatedNotActive || [];

            // Apply role-based agent visibility
            if (role === 'manager') {
              var managerAgentSet = new Set((visibleAgentNames || []).map(function(n) { return normAgent(n); }).filter(Boolean));
              sourceRows = sourceRows.filter(function(r) {
                var curr = r.agent || '';
                return managerAgentSet.has(normAgent(curr));
              });
            }

            // Create a map of notactive metadata by cu key for quick lookup
            var notActiveMetadata = {};
            (notActiveHook.data || []).forEach(function(meta) {
              if (meta.cu) notActiveMetadata[meta.cu] = meta;
            });

            var naCountryOptions = Array.from(new Set(sourceRows.map(function(r) { return r.country; }).filter(Boolean))).sort();
            var naAccTypeOptions = Array.from(new Set(sourceRows.map(function(r) { return r.accountType; }).filter(Boolean))).sort();

            // Enrich portfolio rows with notactive metadata
            var notActiveRows = sourceRows.map(function(r) {
              var meta = notActiveMetadata[r.cu] || {};
              var notActiveDays = daysSinceDate(r.lastAccess);
              return Object.assign({}, r, {
                currentAgent: meta.currentAgent || r.agent || '',
                lastAccessDate: parseDateOnly(r.lastAccess),
                notActiveDays: notActiveDays,
                leadStatus: meta.status || '',
                leadAction: meta.action || '',
                leadComments: meta.comments || '',
                leadUpdatedAt: meta.updatedAt || meta.createdAt || '',
                 newAgent: meta.newAgent || '',
                tVolume: Number(r.tVolume) || 0,
                _notActiveMetaId: meta._id,
              });
            });

            notActiveRows = notActiveRows.filter(function(r) {
              return r.notActiveDays != null && r.notActiveDays > 90;
            });

            // Agent visibility: show only if user is selected as the new reassigned agent
            if (role === 'agent') {
              var myAgentName = userProfile ? (userProfile.dataAgentName || userProfile.name) : '';
              var myAgentSet = new Set([myAgentName, userProfile ? userProfile.name : '', userProfile ? userProfile.uid : ''].map(function(n) { return normAgent(n); }).filter(Boolean));
              notActiveRows = notActiveRows.filter(function(r) {
                var curr = normAgent(r.currentAgent || r.agent || '');
                var reassigned = normAgent(r.newAgent || '');
                return !!reassigned && myAgentSet.has(reassigned) && !myAgentSet.has(curr);
              });
            }

            if (role !== 'agent' && fAgent.length) {
              notActiveRows = notActiveRows.filter(function(r) { return fAgent.indexOf(r.currentAgent) >= 0; });
            }

            if (pCountry.length) {
              notActiveRows = notActiveRows.filter(function(r) { return pCountry.indexOf(r.country) >= 0; });
            }

            if (pAccType.length) {
              notActiveRows = notActiveRows.filter(function(r) { return pAccType.indexOf(r.accountType) >= 0; });
            }

            if (fStatus.length) {
              notActiveRows = notActiveRows.filter(function(r) {
                return (fStatus.indexOf('— Empty —') >= 0 && !r.leadStatus) || (r.leadStatus && fStatus.indexOf(r.leadStatus) >= 0);
              });
            }

            if (fAction.length) {
              notActiveRows = notActiveRows.filter(function(r) {
                return (fAction.indexOf('— Empty —') >= 0 && !r.leadAction) || (r.leadAction && fAction.indexOf(r.leadAction) >= 0);
              });
            }

            notActiveRows = filterSearch(filterDate(notActiveRows, 'lastAccessDate'));

            var uniqueClients = new Set(notActiveRows.map(function(r) { return r.cu; }).filter(Boolean)).size;
            var rowsWithStatus = notActiveRows.filter(function(r) { return !!r.leadStatus; }).length;
            var rowsWithAction = notActiveRows.filter(function(r) { return !!r.leadAction; }).length;

            var notActiveColsCommon = [
              { key: 'cu', label: ll.cuAccount, render: function(r) { return <CopyCell value={r.cu} color={th.accent} />; } },
              { key: 'login', label: ll.loginNo, style: { fontSize: 11, fontWeight: 600 } },
              { key: 'name', label: ll.clientName, style: { fontWeight: 600, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
              { key: 'phone', label: ll.phone, style: { fontSize: 11, direction: 'ltr' }, render: function(r) { return <CopyCell value={r.phone || ''} />; } },
              { key: 'accountType', label: 'Acc.Type', style: { fontSize: 10, whiteSpace: 'nowrap' } },
              { key: 'country', label: 'Country', style: { fontSize: 10, whiteSpace: 'nowrap' } },
              { key: 'tVolume', label: ll.tVolume || 'T.Volume', render: function(r) {
                return <span style={{ fontSize: 11, fontWeight: 700 }}>{(Number(r.tVolume) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>;
              } },
              { key: 'notActiveDays', label: 'Not Active Days', render: function(r) {
                return <span style={{ color: r.notActiveDays > 180 ? '#c0392b' : '#e67e22', fontWeight: 700 }}>{r.notActiveDays}</span>;
              } },
              { key: 'lastAccessDate', label: 'Last Access', style: { fontSize: 11, color: th.sub } },
              { key: 'leadStatus', label: ll.status, render: function(r) {
                if (!r.leadStatus) return <span style={{ color: '#aaa', fontSize: 10 }}>—</span>;
                return <StatusBadge value={r.leadStatus} />;
              } },
              { key: 'leadAction', label: ll.action, render: function(r) {
                if (!r.leadAction) return <span style={{ color: '#aaa', fontSize: 10 }}>—</span>;
                return <ActionBadge value={r.leadAction} />;
              } },
              { key: 'leadComments', label: ll.comments, render: function(r) {
                if (!r.leadComments) return <span style={{ color: '#aaa', fontSize: 10 }}>—</span>;
                return <div onClick={function() { setCommentPopup(r.leadComments); }} style={{ maxWidth: 180, fontSize: 11, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '1.4', cursor: 'pointer', color: '#2b6cb0', textDecoration: 'underline dotted' }}>{r.leadComments}</div>;
              } },
              { key: 'leadUpdatedAt', label: ll.lastUpdated, render: function(r) { return <span style={{ fontSize: 11, color: th.sub }}>{parseDateOnly(r.leadUpdatedAt) || '—'}</span>; } },
              { key: '_actions', label: 'Actions', sortable: false, render: function(r) {
                return (
                  <button onClick={function() {
                    setEditLead({
                      _id: r._notActiveMetaId,
                      _cuKey: r.cu,
                      _notActiveMode: true,
                      client: r.name || '',
                      phone: r.phone || '',
                      currentAgent: r.agent || '',
                       newAgent: r.newAgent || '',
                      status: r.leadStatus || '',
                      action: r.leadAction || '',
                      comments: r.leadComments || '',
                      reminderDate: '',
                      reminderTime: '',
                      date: localNow().slice(0, 10),
                    });
                  }}
                    style={{ padding: '3px 10px', borderRadius: 4, border: 'none', background: '#2b6cb0', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                );
              } },
            ];

            var notActiveCols = role === 'agent'
              ? notActiveColsCommon.concat([
                { key: 'newAgent', label: 'Reassigned Agent', style: { fontSize: 11 }, render: function(r) { return r.newAgent ? <span style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed' }}>{r.newAgent}</span> : <span style={{ color: '#aaa', fontSize: 10 }}>—</span>; } },
              ])
              : [
                notActiveColsCommon[0],
                notActiveColsCommon[1],
                notActiveColsCommon[2],
                notActiveColsCommon[3],
                notActiveColsCommon[4],
                notActiveColsCommon[5],
                { key: 'agent', label: 'Current Agent', style: { fontSize: 11 } },
                { key: 'newAgent', label: 'New Agent', style: { fontSize: 11 }, render: function(r) { return r.newAgent ? <span style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed' }}>{r.newAgent}</span> : <span style={{ color: '#aaa', fontSize: 10 }}>—</span>; } },
                notActiveColsCommon[6],
                notActiveColsCommon[7],
                notActiveColsCommon[8],
                notActiveColsCommon[9],
                notActiveColsCommon[10],
                notActiveColsCommon[11],
                notActiveColsCommon[12],
                notActiveColsCommon[13],
              ];

            return (
              <div>
                {renderFilters({ showStatus: false, showAction: false, extraFilters: <React.Fragment>
                  <div style={{ minWidth: 130 }}>
                    <MultiDropdown value={pCountry} options={naCountryOptions}
                      onChange={function(v) { setPCountry(v); }}
                      placeholder="All Countries" th={th} />
                  </div>
                  <div style={{ minWidth: 130 }}>
                    <MultiDropdown value={pAccType} options={naAccTypeOptions}
                      onChange={function(v) { setPAccType(v); }}
                      placeholder="All Account Types" th={th} />
                  </div>
                  <div style={{ minWidth: 130 }}>
                    <MultiDropdown value={fStatus} options={['— Empty —'].concat(STATUSES)}
                      onChange={function(v) { setFStatus(v); }}
                      placeholder={ll.allStatuses} th={th} />
                  </div>
                  <div style={{ minWidth: 130 }}>
                    <MultiDropdown value={fAction} options={['— Empty —'].concat(ACTIONS)}
                      onChange={function(v) { setFAction(v); }}
                      placeholder={ll.allActions || 'All Actions'} th={th} />
                  </div>
                </React.Fragment> })}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#e74c3c' }}>{'Total Logins: ' + notActiveRows.length}</div>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#6366f1' }}>{'Total Clients: ' + uniqueClients}</div>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#8b5cf6' }}>{'With Status: ' + rowsWithStatus}</div>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{'With Action: ' + rowsWithAction}</div>
                  <button onClick={function() { handleExport(notActiveRows, 'not_active_clients'); }} style={{ marginInlineStart: 'auto', padding: '6px 14px', borderRadius: 6, border: '1px solid ' + th.border, background: '#1a365d', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{'↓ ' + ll.exportData}</button>
                </div>
                <SortableTable th={th} rtl={rtl} empty={ll.noData} data={notActiveRows} cols={notActiveCols} />
              </div>
            );
          })()}

          {/* ═══════ FOLLOW UP ═══════ */}
          {tab === 'followup' && (function() {
            if (followupsHook.loading) return <Spinner th={th} text={ll.loading} />;
            var myFollowupsRaw = role === 'admin' ? followupsHook.data : role === 'manager'
              ? followupsHook.data.filter(function(f) { return visibleAgentNames.indexOf(f.agent) >= 0; })
              : followupsHook.data.filter(function(f) { return f.agent === (userProfile ? (userProfile.dataAgentName || userProfile.name) : ''); });

            // Keep one follow-up per login (latest updatedAt) to avoid duplicate rows.
            var byLogin = {};
            myFollowupsRaw.forEach(function(f) {
              var k = String(f.login || '');
              if (!k) return;
              if (!byLogin[k] || (f.updatedAt || '') > (byLogin[k].updatedAt || '')) byLogin[k] = f;
            });
            var myFollowups = Object.values(byLogin);

            // Apply filters
            var filtered = myFollowups;
            if (search) {
              var s = search.toLowerCase();
              filtered = filtered.filter(function(f) {
                return (f.cu || '').toLowerCase().indexOf(s) >= 0 || (f.login || '').toLowerCase().indexOf(s) >= 0 || (f.name || '').toLowerCase().indexOf(s) >= 0 || (f.comment || '').toLowerCase().indexOf(s) >= 0;
              });
            }
            if (fAgent.length) filtered = filtered.filter(function(f) { return fAgent.indexOf(f.agent) >= 0; });
            if (fuFilterStatus.length) filtered = filtered.filter(function(f) { return (fuFilterStatus.indexOf('— Empty —') >= 0 && !f.status) || (f.status && fuFilterStatus.indexOf(f.status) >= 0); });
            if (fuFilterAction.length) filtered = filtered.filter(function(f) { return (fuFilterAction.indexOf('— Empty —') >= 0 && !f.action) || (f.action && fuFilterAction.indexOf(f.action) >= 0); });
            if (dateFrom) filtered = filtered.filter(function(f) { return (f.updatedAt || '') >= dateFrom; });
            if (dateTo) filtered = filtered.filter(function(f) { return (f.updatedAt || '').slice(0, 10) <= dateTo; });

            // Sort by updatedAt descending (newest first)
            filtered = filtered.slice().sort(function(a, b) { return (b.updatedAt || '').localeCompare(a.updatedAt || ''); });

            return (
              <div>
                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ flex: '1 1 160px', minWidth: 140, position: 'relative' }}>
                    <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder={ll.search}
                      style={{ width: '100%', padding: '8px 10px 8px 28px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
                    <span style={{ position: 'absolute', left: rtl ? 'auto' : 9, right: rtl ? 9 : 'auto', top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: th.sub, pointerEvents: 'none' }}>&#8981;</span>
                  </div>
                  {role !== 'agent' && allAgentNamesFromData.length > 0 && (
                    <div style={{ minWidth: 140 }}>
                      <MultiDropdown value={fAgent} options={allAgentNamesFromData}
                        onChange={function(v) { setFAgent(v); }}
                        placeholder={ll.allAgents} th={th} />
                    </div>
                  )}
                  <div style={{ minWidth: 130 }}>
                    <MultiDropdown value={fuFilterStatus} options={['— Empty —'].concat(FU_STATUSES)}
                      onChange={function(v) { setFuFilterStatus(v); }}
                      placeholder="All Status" th={th} />
                  </div>
                  <div style={{ minWidth: 130 }}>
                    <MultiDropdown value={fuFilterAction} options={['— Empty —'].concat(FU_ACTIONS)}
                      onChange={function(v) { setFuFilterAction(v); }}
                      placeholder="All Actions" th={th} />
                  </div>
                  <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }}
                    style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                  <span style={{ fontSize: 11, color: th.sub }}>to</span>
                  <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }}
                    style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                  {(search || fAgent.length || fuFilterStatus.length || fuFilterAction.length || dateFrom || dateTo) && (
                    <button onClick={function() { setSearch(''); setFAgent([]); setFuFilterStatus([]); setFuFilterAction([]); setDateFrom(''); setDateTo(''); }}
                      style={{ background: 'none', border: '1px solid ' + th.border, borderRadius: 6, padding: '7px 12px', fontSize: 11, cursor: 'pointer', color: th.sub }}>{'x ' + ll.clear}</button>
                  )}
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#2b6cb0' }}>{'Total: ' + filtered.length}</div>
                  {FU_STATUSES.map(function(s) {
                    var c = filtered.filter(function(f) { return f.status === s; }).length;
                    if (c === 0) return null;
                    var col = FU_STATUS_COLORS[s] || {};
                    return <div key={s} style={{ background: col.bg || th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 11, fontWeight: 600, color: col.tx || th.text }}>{s + ': ' + c}</div>;
                  })}
                </div>

                {/* Export */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <button onClick={function() { exportToExcel(filtered.map(function(f) { return { CU: f.cu, Login: f.login, 'Client Name': f.name, Agent: f.agent, Status: f.status, Action: f.action, Comment: f.comment, 'Last Updated': f.updatedAt, 'Updated By': f.updatedBy }; }), 'FollowUp_Export'); }}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid ' + th.border, background: '#1a365d', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{'↓ ' + ll.exportData}</button>
                </div>

                {/* Table */}
                <SortableTable th={th} rtl={rtl} empty={ll.noData} data={filtered} cols={[
                  { key: 'cu', label: 'CU Account', render: function(r) { return <CopyCell value={r.cu} color={th.accent} />; } },
                  { key: 'login', label: 'Login', style: { fontSize: 11, fontWeight: 600 } },
                  { key: 'name', label: ll.clientName, style: { fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                  { key: 'status', label: ll.status, render: function(r) {
                    var col = FU_STATUS_COLORS[r.status] || {};
                    return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: col.bg || '#e2e3e5', color: col.tx || '#383d41' }}>{r.status || '—'}</span>;
                  }},
                  { key: 'action', label: ll.action, render: function(r) {
                    var col = FU_ACTION_COLORS[r.action] || {};
                    return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: col.bg || '#e2e3e5', color: col.tx || '#383d41' }}>{r.action || '—'}</span>;
                  }},
                  { key: 'comment', label: ll.comments, render: function(r) {
                    if (!r.comment) return <span style={{ color: '#aaa', fontSize: 10 }}>—</span>;
                    return <div onClick={function() { setCommentPopup(r.comment); }} style={{ maxWidth: 180, fontSize: 11, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '1.4', cursor: 'pointer', color: '#2b6cb0', textDecoration: 'underline dotted' }}>{r.comment}</div>;
                  } },
                  { key: 'updatedAt', label: ll.lastUpdated, render: function(r) { return <span style={{ fontSize: 10, color: th.sub }}>{localFmt(r.updatedAt) || '—'}</span>; } },
                ].concat(role !== 'agent' ? [{ key: 'agent', label: ll.accountManager, style: { fontSize: 11 } }] : []).concat([
                  { key: 'updatedBy', label: ll.updatedBy, style: { fontSize: 10, color: th.sub } },
                  { key: '_actions', label: 'Actions', sortable: false, render: function(r) {
                    if (role === 'manager') return <span style={{ fontSize: 10, color: th.sub }}>Read only</span>;
                    return (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={function() { setFuModal(Object.assign({}, r)); }}
                          style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid ' + th.border, background: '#2b6cb0', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>💬 Edit</button>
                        {role === 'admin' && (
                          <button onClick={function() { if (window.confirm('Delete follow-up for "' + r.name + '"?')) { followupsHook.deleteItem(r._id); showMsg('Deleted'); } }}
                            style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Del</button>
                        )}
                      </div>
                    );
                  }},
                ])} rowClassName={function(r) { return r._id === highlightId ? 'highlight-row' : ''; }} />
              </div>
            );
          })()}

          {/* ═══════ RETENTION ═══════ */}
          {tab === 'retention' && (function() {
            if (portfolioLoading) return <Spinner th={th} text={ll.loading} />;
            var retData = fAgent.length ? myRetention.filter(function(x) { return fAgent.indexOf(x.agent) >= 0; }) : myRetention;
            // Apply date filter on reactivationDate
            if (dateFrom) retData = retData.filter(function(x) { return (x.reactivationDate || '') >= dateFrom; });
            if (dateTo) retData = retData.filter(function(x) { return (x.reactivationDate || '').slice(0, 10) <= dateTo; });
            // Apply search
            if (search) {
              var s = search.toLowerCase();
              retData = retData.filter(function(x) {
                return (x.cu && x.cu.toLowerCase().indexOf(s) >= 0) ||
                       (x.name && x.name.toLowerCase().indexOf(s) >= 0) ||
                       (String(x.login).toLowerCase().indexOf(s) >= 0);
              });
            }
            var fmtN = function(v) { return (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); };
            return (
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
                  <div style={{ flex: '1 1 160px', minWidth: 140, position: 'relative' }}>
                    <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder={ll.search}
                      style={{ width: '100%', padding: '8px 10px 8px 28px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
                    <span style={{ position: 'absolute', left: rtl ? 'auto' : 9, right: rtl ? 9 : 'auto', top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: th.sub, pointerEvents: 'none' }}>&#8981;</span>
                  </div>
                  {role !== 'agent' && allAgentNamesFromData.length > 0 && (
                    <div style={{ minWidth: 140 }}>
                      <MultiDropdown value={fAgent} options={allAgentNamesFromData}
                        onChange={function(v) { setFAgent(v); }}
                        placeholder={ll.allAgents} th={th} />
                    </div>
                  )}
                  <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }}
                    style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                  <span style={{ color: th.sub, fontSize: 11 }}>to</span>
                  <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }}
                    style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                  {(search || fAgent.length || dateFrom || dateTo) && (
                    <button onClick={function() { setSearch(''); setFAgent([]); setDateFrom(''); setDateTo(''); }}
                      style={{ background: 'none', border: '1px solid ' + th.border, borderRadius: 6, padding: '7px 12px', fontSize: 11, cursor: 'pointer', color: th.sub }}>{'x ' + ll.clear}</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ background: th.card, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + th.border, fontSize: 12, fontWeight: 700, color: '#0ea5e9' }}>{'🔁 Total: ' + retData.length}</div>
                </div>
                <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[
                  { key: 'cu', label: 'CU Account', render: function(r) { return <CopyCell value={r.cu} color={th.accent} />; } },
                  { key: 'login', label: 'Login', style: { fontSize: 11 } },
                  { key: 'name', label: 'Client Name', style: { fontWeight: 600 } },
                  { key: 'isReactivated', label: 'Reactivated', render: function(r) {
                    return <span style={{ background: '#d1fae5', color: '#059669', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, display: 'inline-block' }}>Yes</span>;
                  }},
                  { key: 'reactivationDate', label: 'Reactivation Date', style: { fontSize: 11 } },
                  { key: 'volume', label: 'Volume', render: function(r) { return <span style={{ fontWeight: 600 }}>{fmtN(r.volume)}</span>; } },
                ].concat(role !== 'agent' ? [{ key: 'agent', label: 'Agent', style: { fontSize: 11 } }] : [])} data={retData} />
              </div>
            );
          })()}

          {/* ═══════ USERS (Admin) ═══════ */}
          {tab === 'users' && role === 'admin' && (
            <div>
              <div style={{ marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={function() { setShowAddUser(true); }} style={{ background: th.accent, color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {'+ ' + ll.addUser}
                </button>
                <button onClick={function() {
                  try {
                    var backupResult = backupAllData({
                      portfolio: portfolioData,
                      accounts: accountsHook.data,
                      ftd: ftdHook.data,
                      leads: leadsHook.data,
                      followups: followupsHook.data,
                      notactive: notActiveHook.data,
                      users: allUsers,
                      retention: myRetention
                    });
                    if (backupResult && backupResult.warnings && backupResult.warnings.length) {
                      console.warn('Backup warnings:', backupResult.warnings);
                      showMsg('Backup downloaded as ' + ((backupResult && backupResult.fileType) || 'xlsx') + ' with ' + backupResult.warnings.length + ' warning(s). Check console for details.');
                    } else {
                      var msg = 'Backup downloaded as ' + ((backupResult && backupResult.fileType) || 'xlsx') + '! (' + (backupResult && backupResult.sheetCount ? backupResult.sheetCount + ' sheets' : 'complete') + ')';
                      showMsg(msg);
                    }
                  } catch (err) {
                    console.error('Backup failed:', err);
                    showMsg('Backup failed: ' + (err && err.message ? err.message : 'Unknown error'));
                  }
                }} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {'⬇ Backup All Data'}
                </button>
                <label style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
                  {'⬆ Restore from Backup'}
                  <input type="file" accept=".xlsx" style={{ display: 'none' }} onChange={function(e) {
                    var file = e.target.files[0];
                    if (!file) return;
                    if (!window.confirm('⚠️ This will IMPORT Leads & Follow-Ups from the backup file.\n\nExisting data will NOT be deleted — backup data will be ADDED.\n\nContinue?')) { e.target.value = ''; return; }
                    setUploading(true);
                    showMsg('Restoring from backup...');
                    parseBackupExcel(file).then(function(result) {
                      var promises = [];
                      if (result.leads.length > 0) {
                        promises.push(leadsHook.bulkUpload(result.leads.map(function(d) { return Object.assign({}, d, { uploadedBy: 'Restore' }); })));
                      }
                      if (result.followups.length > 0) {
                        promises.push(followupsHook.bulkUpload(result.followups));
                      }
                      Promise.all(promises).then(function() {
                        showMsg('✅ Restored: ' + result.leads.length + ' leads + ' + result.followups.length + ' follow-ups');
                        setUploading(false);
                      });
                    }).catch(function(err) {
                      showMsg('Restore failed: ' + err.message);
                      setUploading(false);
                      console.error(err);
                    });
                    e.target.value = '';
                  }} />
                </label>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: th.text, marginBottom: 10 }}>{ll.manager + 's'}</div>
              <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[
                { key: 'name', label: ll.name, style: { fontWeight: 600 } },
                { key: 'email', label: ll.email, style: { fontSize: 11, direction: 'ltr' } },
                { key: 'role', label: ll.role, render: function(r) { return <RoleBadge role={r.role} />; } },
                { key: 'agentList', label: ll.assignedAgents, style: { fontSize: 11 } },
                { key: '_actions', label: 'Actions', sortable: false, render: function(r) {
                  return (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={function() { setEditUser(Object.assign({}, r, { assignedAgents: r.assignedAgents || [] })); }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid ' + th.border, background: '#2b6cb0', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                      <button onClick={function() { resetPassword(r.email).then(function() { showMsg('Reset email sent to ' + r.email); }).catch(function(e) { showMsg(e.message); }); }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Reset PW</button>
                      <button onClick={function() { if (window.confirm('Delete user "' + r.name + '"?')) { deleteUser(r.uid).then(function() { showMsg('Deleted'); getAllUsers().then(setAllUsers); }); } }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Del</button>
                    </div>
                  );
                }},
              ]} data={allUsers.filter(function(u) { return u.role === 'manager'; }).map(function(u) {
                var names = (u.assignedAgents || []).map(function(aid) {
                  var found = allUsers.find(function(a) { return a.uid === aid || a.name === aid; });
                  return found ? found.name : aid;
                });
                return Object.assign({}, u, { agentList: names.join(', ') });
              })} />
              <div style={{ fontSize: 13, fontWeight: 700, color: th.text, margin: '20px 0 10px' }}>{ll.agent + 's'}</div>
              <SortableTable th={th} rtl={rtl} empty={ll.noData} cols={[
                { key: 'name', label: ll.name, style: { fontWeight: 600 } },
                { key: 'dataAgentName', label: 'Data Name', render: function(r) { return r.dataAgentName ? <span style={{ fontSize: 11, color: '#16835e', fontWeight: 600 }}>{r.dataAgentName}</span> : <span style={{ fontSize: 10, color: '#e74c3c' }}>⚠ Not set</span>; } },
                { key: 'email', label: ll.email, style: { fontSize: 11, direction: 'ltr' } },
                { key: 'role', label: ll.role, render: function(r) { return <RoleBadge role={r.role} />; }, sortable: false },
                { key: '_actions', label: 'Actions', sortable: false, render: function(r) {
                  return (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={function() { setEditUser(Object.assign({}, r, { _origDataName: r.dataAgentName || '' })); }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid ' + th.border, background: '#2b6cb0', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                      <button onClick={function() { resetPassword(r.email).then(function() { showMsg('Reset email sent to ' + r.email); }).catch(function(e) { showMsg(e.message); }); }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Reset PW</button>
                      <button onClick={function() { if (window.confirm('Delete user "' + r.name + '"?')) { deleteUser(r.uid).then(function() { showMsg('Deleted'); getAllUsers().then(setAllUsers); }); } }}
                        style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Del</button>
                    </div>
                  );
                }},
              ]} data={allUsers.filter(function(u) { return u.role === 'agent'; })} />
            </div>
          )}
        </div>

        {/* ADD LEAD MODAL */}
        <Modal show={showAddLead} onClose={function() { setShowAddLead(false); }} title={'+ ' + ll.addNewLead} th={th}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.clientName}</label>
              <input value={newLead.client} onChange={function(e) { setNewLead(Object.assign({}, newLead, { client: e.target.value })); }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.phone}</label>
              <input value={newLead.phone} onChange={function(e) { setNewLead(Object.assign({}, newLead, { phone: e.target.value })); }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box', direction: 'ltr' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.accountManager}</label>
              <Dropdown value={newLead.agent} options={allAgentNamesFromData} onChange={function(v) { setNewLead(Object.assign({}, newLead, { agent: v })); }} placeholder={ll.selectAgent2} th={th} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.date}</label>
              <input type="date" value={newLead.date} onChange={function(e) { setNewLead(Object.assign({}, newLead, { date: e.target.value })); }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button onClick={function() { setShowAddLead(false); }} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: th.sub }}>{ll.cancel}</button>
            <button onClick={handleAddLead} style={{ flex: 1, padding: 10, borderRadius: 6, border: 'none', background: th.accent, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!newLead.client || !newLead.phone || !newLead.agent) ? 0.4 : 1 }}>{ll.addLead}</button>
          </div>
        </Modal>

        {/* EDIT LEAD MODAL */}
        <Modal show={!!editLead} onClose={function() { setEditLead(null); }} title={editLead && editLead._notActiveMode ? 'Edit Not Active' : (role === 'manager' ? 'Assign Agent' : 'Edit Lead')} th={th}>
          {editLead && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Lead info header (always shown, read-only for manager) */}
                <div style={{ background: th.bg, borderRadius: 8, padding: 10, border: '1px solid ' + th.border }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: th.text }}>{editLead.client}</div>
                  <div style={{ fontSize: 11, color: th.sub, direction: 'ltr', display: 'inline-block' }}>{editLead.phone}</div>
                  {editLead.date && <div style={{ fontSize: 10, color: th.sub, marginTop: 2 }}>{editLead.date}</div>}
                </div>
                {/* Admin: editable client name */}
                {(role === 'admin' && !(editLead && editLead._notActiveMode)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.clientName}</label>
                  <input value={editLead.client} onChange={function(e) { setEditLead(Object.assign({}, editLead, { client: e.target.value })); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
                </div>
                )}
                {/* Admin: editable phone */}
                {(role === 'admin' && !(editLead && editLead._notActiveMode)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.phone}</label>
                  <input value={editLead.phone} onChange={function(e) { setEditLead(Object.assign({}, editLead, { phone: e.target.value })); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box', direction: 'ltr' }} />
                </div>
                )}
                {/* Current agent is read-only in Not Active mode */}
                {(role !== 'agent' && editLead && editLead._notActiveMode) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>Current Agent</label>
                  <div style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.bg, fontSize: 12, color: th.text, boxSizing: 'border-box' }}>
                    {editLead.currentAgent || '—'}
                  </div>
                </div>
                )}
                {/* Agent dropdown for non-Not-Active flows */}
                {(role !== 'agent' && !(editLead && editLead._notActiveMode)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.accountManager}</label>
                  <Dropdown value={editLead.agent} options={allAgentNamesFromData} onChange={function(v) { setEditLead(Object.assign({}, editLead, { agent: v })); }} placeholder={ll.selectAgent2} th={th} />
                </div>
                )}
                  {/* New Agent dropdown (for not-active reassignment) */}
                  {(editLead && editLead._notActiveMode && (role === 'admin' || role === 'manager')) && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>New Agent (Reassign To)</label>
                    <Dropdown value={editLead.newAgent || 'Unassigned'} options={['Unassigned'].concat(allAgentNamesFromData)} onChange={function(v) { setEditLead(Object.assign({}, editLead, { newAgent: v === 'Unassigned' ? '' : v })); }} placeholder="Select new agent" th={th} />
                  </div>
                  )}
                {/* Admin: status */}
                {(role === 'admin' || (editLead && editLead._notActiveMode)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.status}</label>
                  <Dropdown value={editLead.status} options={STATUSES} onChange={function(v) { setEditLead(Object.assign({}, editLead, { status: v })); }} placeholder={ll.selectStatus} th={th} />
                </div>
                )}
                {/* Admin: action */}
                {(role === 'admin' || (editLead && editLead._notActiveMode)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.action}</label>
                  <Dropdown value={editLead.action} options={ACTIONS} onChange={function(v) { setEditLead(Object.assign({}, editLead, { action: v })); }} placeholder={ll.selectAction} th={th} />
                </div>
                )}
                {/* Admin: comments */}
                {(role === 'admin' || (editLead && editLead._notActiveMode)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.comments}</label>
                  <textarea value={editLead.comments || ''} onChange={function(e) { if (e.target.value.length <= 200) setEditLead(Object.assign({}, editLead, { comments: e.target.value })); }}
                    maxLength={200}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box', minHeight: 60, resize: 'vertical' }} />
                  <div style={{ fontSize: 9, color: (editLead.comments || '').length >= 180 ? '#e74c3c' : th.sub, textAlign: 'right', marginTop: 2 }}>{(editLead.comments || '').length + ' / 200'}</div>
                </div>
                )}
                {/* Reminder (admin + manager) */}
                {(role === 'admin' || role === 'manager') && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>🔔 Reminder</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="date" value={editLead.reminderDate || ''} onChange={function(e) { setEditLead(Object.assign({}, editLead, { reminderDate: e.target.value })); }}
                      style={{ flex: '1 1 130px', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                    <input type="time" value={editLead.reminderTime || ''} onChange={function(e) { setEditLead(Object.assign({}, editLead, { reminderTime: e.target.value })); }}
                      style={{ flex: '1 1 100px', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                    {(editLead.reminderDate || editLead.reminderTime) && (
                      <button onClick={function() { setEditLead(Object.assign({}, editLead, { reminderDate: '', reminderTime: '' })); }}
                        style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Clear</button>
                    )}
                  </div>
                </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={function() { setEditLead(null); }} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: th.sub }}>{ll.cancel}</button>
                <button onClick={function() {
                  if (editLead && editLead._notActiveMode) {
                    var payload = {
                      cu: editLead._cuKey,
                      name: editLead.client,
                      phone: editLead.phone,
                      status: editLead.status || '',
                      action: editLead.action || '',
                      comments: editLead.comments || '',
                       newAgent: editLead.newAgent || '',
                      reminderDate: editLead.reminderDate || '',
                      reminderTime: editLead.reminderTime || '',
                      updatedBy: userProfile.name,
                      updatedAt: localNow(),
                    };
                    if (editLead._id) {
                      // Update existing metadata record
                      notActiveHook.updateItem(editLead._id, payload);
                    } else {
                      // Create new metadata record
                      notActiveHook.addItem(payload);
                    }
                  } else if (role === 'manager') {
                    leadsHook.updateItem(editLead._id, { agent: editLead.agent, date: localNow().slice(0, 10), reminderDate: editLead.reminderDate || '', reminderTime: editLead.reminderTime || '' });
                  } else {
                    leadsHook.updateItem(editLead._id, { client: editLead.client, phone: editLead.phone, agent: editLead.agent, date: localNow().slice(0, 10), status: editLead.status, action: editLead.action, comments: editLead.comments, reminderDate: editLead.reminderDate || '', reminderTime: editLead.reminderTime || '' });
                  }
                  showMsg(ll.saved);
                  setEditLead(null);
                }} style={{ flex: 1, padding: 10, borderRadius: 6, border: 'none', background: th.accent, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{role === 'manager' && !(editLead && editLead._notActiveMode) ? 'Assign' : 'Save'}</button>
              </div>
            </div>
          )}
        </Modal>

        {/* FOLLOW UP MODAL */}
        <Modal show={!!fuModal} onClose={function() { setFuModal(null); }} title={fuModal && fuModal._id ? ll.editFollowUp : ll.addFollowUp} th={th}>
          {fuModal && (
            <div>
              {/* Client info (read-only) */}
              <div style={{ background: th.stripe, borderRadius: 8, padding: 10, marginBottom: 12, border: '1px solid ' + th.border }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                  <div><span style={{ fontWeight: 600, color: th.sub }}>CU: </span><span style={{ fontWeight: 700, color: th.accent }}>{fuModal.cu}</span></div>
                  <div><span style={{ fontWeight: 600, color: th.sub }}>Login: </span><span style={{ fontWeight: 700 }}>{fuModal.login}</span></div>
                  <div><span style={{ fontWeight: 600, color: th.sub }}>Name: </span><span style={{ fontWeight: 600 }}>{fuModal.name}</span></div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.followUpStatus}</label>
                  <Dropdown value={fuModal.status} options={FU_STATUSES} onChange={function(v) { setFuModal(Object.assign({}, fuModal, { status: v })); }} placeholder="Select Status" th={th} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.followUpAction}</label>
                  <Dropdown value={fuModal.action} options={FU_ACTIONS} onChange={function(v) { setFuModal(Object.assign({}, fuModal, { action: v })); }} placeholder="Select Action" th={th} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.followUpComment}</label>
                  <textarea value={fuModal.comment || ''} onChange={function(e) { setFuModal(Object.assign({}, fuModal, { comment: e.target.value })); }}
                    placeholder={ll.writeComment}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>🔔 Reminder</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="date" value={fuModal.reminderDate || ''} onChange={function(e) { setFuModal(Object.assign({}, fuModal, { reminderDate: e.target.value })); }}
                      style={{ flex: '1 1 130px', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                    <input type="time" value={fuModal.reminderTime || ''} onChange={function(e) { setFuModal(Object.assign({}, fuModal, { reminderTime: e.target.value })); }}
                      style={{ flex: '1 1 100px', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none' }} />
                    {(fuModal.reminderDate || fuModal.reminderTime) && (
                      <button onClick={function() { setFuModal(Object.assign({}, fuModal, { reminderDate: '', reminderTime: '' })); }}
                        style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: '#e74c3c', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Clear</button>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={function() { setFuModal(null); }} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: th.sub }}>{ll.cancel}</button>
                <button onClick={function() {
                  if (!fuModal.status && !fuModal.action && !fuModal.comment) return;
                  var now = localNow();
                  var data = { cu: fuModal.cu, login: String(fuModal.login), name: fuModal.name, agent: fuModal.agent || (userProfile ? userProfile.name : ''), status: fuModal.status, action: fuModal.action, comment: fuModal.comment || '', reminderDate: fuModal.reminderDate || '', reminderTime: fuModal.reminderTime || '', updatedAt: now, updatedBy: userProfile ? userProfile.name : 'Unknown' };
                  // Clear fired ref so updated reminder can fire again
                  if (fuModal._id) delete firedRemindersRef.current['fu_' + fuModal._id];
                  if (fuModal._id) {
                    followupsHook.updateItem(fuModal._id, data).then(function() { showMsg(ll.saved); setFuModal(null); });
                  } else {
                    followupsHook.addItem(data).then(function() { showMsg(ll.saved); setFuModal(null); });
                  }
                }} style={{ flex: 1, padding: 10, borderRadius: 6, border: 'none', background: th.accent, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!fuModal.status && !fuModal.action && !fuModal.comment) ? 0.4 : 1 }}>{ll.save}</button>
              </div>
            </div>
          )}
        </Modal>

        {/* ADD USER MODAL */}
        <Modal show={showAddUser} onClose={function() { setShowAddUser(false); setCreatedCreds(null); }} title={createdCreds ? 'User Created!' : '+ ' + ll.addUser} th={th}>
          {createdCreds ? (
            <div>
              <div style={{ background: '#f0fdf4', border: '1px solid #16835e', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#16835e', marginBottom: 10 }}>Account created successfully!</div>
                <div style={{ fontSize: 12, color: '#333', lineHeight: 1.8 }}>
                  <div><strong>Name:</strong> {createdCreds.name}</div>
                  <div><strong>Email:</strong> {createdCreds.email}</div>
                  <div><strong>Password:</strong> {createdCreds.password}</div>
                  <div><strong>Role:</strong> {createdCreds.role}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={function() {
                  var text = 'Welcome to River Prime LTD CRM!\n\nYour login credentials:\nURL: ' + window.location.origin + '\nEmail: ' + createdCreds.email + '\nPassword: ' + createdCreds.password + '\n\nPlease change your password after first login.';
                  navigator.clipboard.writeText(text).then(function() { showMsg('Copied to clipboard!'); });
                }} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#2b6cb0' }}>📋 Copy</button>
                <button onClick={function() {
                  authCtx.sendUserCredentialsEmail({
                    name: createdCreds.name,
                    email: createdCreds.email,
                    password: createdCreds.password,
                  }).then(function() {
                    showMsg('Credentials email sent to ' + createdCreds.email);
                  }).catch(function(e) {
                    showMsg(e.message);
                  });
                }} style={{ flex: 1, padding: 10, borderRadius: 6, border: 'none', background: '#2b6cb0', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📧 Email</button>
              </div>
              <button onClick={function() { setShowAddUser(false); setCreatedCreds(null); }} style={{ width: '100%', marginTop: 8, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: th.sub }}>Close</button>
            </div>
          ) : (
          <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.name}</label>
              <input value={newUser.name} onChange={function(e) { setNewUser(Object.assign({}, newUser, { name: e.target.value })); }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.email}</label>
              <input type="email" value={newUser.email} onChange={function(e) { setNewUser(Object.assign({}, newUser, { email: e.target.value })); }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box', direction: 'ltr' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.password}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="text" value={newUser.password} onChange={function(e) { setNewUser(Object.assign({}, newUser, { password: e.target.value })); }}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box', direction: 'ltr' }} />
                <button onClick={function() { setNewUser(Object.assign({}, newUser, { password: genPassword() })); }}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 11, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>🎲 Generate</button>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.role}</label>
              <Dropdown value={newUser.role} options={['admin', 'manager', 'agent']} onChange={function(v) { setNewUser(Object.assign({}, newUser, { role: v })); }} placeholder={ll.role} th={th} />
            </div>
            {newUser.role === 'manager' && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.selectAgents}</label>
                <div style={{ maxHeight: 150, overflow: 'auto', border: '1px solid ' + th.inpB, borderRadius: 6, padding: 6 }}>
                  {allAgentNamesFromData.map(function(agentName) {
                    var checked = (newUser.assignedAgents || []).indexOf(agentName) >= 0;
                    return (
                      <label key={agentName} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', fontSize: 12, color: th.text, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checked} onChange={function() {
                          var list = newUser.assignedAgents || [];
                          if (checked) list = list.filter(function(n) { return n !== agentName; });
                          else list = list.concat([agentName]);
                          setNewUser(Object.assign({}, newUser, { assignedAgents: list }));
                        }} />
                        {agentName}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button onClick={function() { setShowAddUser(false); }} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: th.sub }}>{ll.cancel}</button>
            <button onClick={async function() {
              if (!newUser.email || !newUser.password || !newUser.name) return;
              try {
                await authCtx.createUser(newUser.email, newUser.password, newUser);
                setCreatedCreds({ name: newUser.name, email: newUser.email, password: newUser.password, role: newUser.role });
                setNewUser({ email: '', password: '', name: '', role: 'agent', assignedAgents: [] });
                setUsersLoaded(false);
              } catch (e) { showMsg(e.message); }
            }} style={{ flex: 1, padding: 10, borderRadius: 6, border: 'none', background: th.accent, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!newUser.email || !newUser.password || !newUser.name) ? 0.4 : 1 }}>{ll.save}</button>
          </div>
          </div>
          )}
        </Modal>

        {/* EDIT USER MODAL */}
        <Modal show={!!editUser} onClose={function() { setEditUser(null); }} title={'Edit User'} th={th}>
          {editUser && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.name}</label>
                  <input value={editUser.name} onChange={function(e) { setEditUser(Object.assign({}, editUser, { name: e.target.value })); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12, outline: 'none', color: th.inpT, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.email}</label>
                  <div style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.stripe, fontSize: 12, color: th.sub, boxSizing: 'border-box', direction: 'ltr' }}>{editUser.email}</div>
                  <div style={{ fontSize: 9, color: th.sub, marginTop: 2 }}>Email can only be changed by backend admin</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>{ll.role}</label>
                  <Dropdown value={editUser.role} options={['admin', 'manager', 'agent']} onChange={function(v) { setEditUser(Object.assign({}, editUser, { role: v })); }} placeholder={ll.role} th={th} />
                </div>
                {(editUser.role === 'agent' || editUser.role === 'manager') && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 3 }}>Agent Name in Data</label>
                    <Dropdown value={editUser.dataAgentName || ''} options={allAgentNamesFromData} onChange={function(v) { setEditUser(Object.assign({}, editUser, { dataAgentName: v })); }} placeholder="Select name from data..." th={th} />
                    <div style={{ fontSize: 9, color: '#e67e22', marginTop: 2 }}>Maps this user to their actual name in uploaded data</div>
                  </div>
                )}
                {editUser.role === 'manager' && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: th.sub, display: 'block', marginBottom: 6 }}>{ll.selectAgents}</label>
                    <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid ' + th.inpB, borderRadius: 6, padding: 6 }}>
                      {(function() {
                        var nameSet = new Set(allAgentNamesFromData);
                        (editUser.assignedAgents || []).forEach(function(n) { nameSet.add(n); });
                        return Array.from(nameSet).sort();
                      })().map(function(agentName) {
                        var checked = (editUser.assignedAgents || []).indexOf(agentName) >= 0;
                        var isOrphan = allAgentNamesFromData.indexOf(agentName) < 0;
                        return (
                          <label key={agentName} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px', fontSize: 12, color: isOrphan ? '#e74c3c' : th.text, cursor: 'pointer', borderRadius: 4 }}
                            onMouseEnter={function(e) { e.currentTarget.style.background = th.stripe; }}
                            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                            <input type="checkbox" checked={checked} onChange={function() {
                              var list = editUser.assignedAgents || [];
                              if (checked) list = list.filter(function(n) { return n !== agentName; });
                              else list = list.concat([agentName]);
                              setEditUser(Object.assign({}, editUser, { assignedAgents: list }));
                            }} style={{ cursor: 'pointer' }} />
                            {agentName}{isOrphan ? ' ⚠️' : ''}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 10, color: th.sub, marginTop: 4 }}>
                      {(editUser.assignedAgents || []).length + ' agents selected'}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={function() { setEditUser(null); }} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid ' + th.border, background: th.card, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: th.sub }}>{ll.cancel}</button>
                <button onClick={async function() {
                  var oldName = editUser._origDataName || editUser.dataAgentName || '';
                  var newName = editUser.dataAgentName || '';
                  await updateUser(editUser.uid, { name: editUser.name, role: editUser.role, assignedAgents: editUser.assignedAgents || [], dataAgentName: newName });
                  // Cascade: if agent's dataAgentName changed, update all managers' assignedAgents
                  if (editUser.role === 'agent' && oldName && newName && oldName !== newName) {
                    var managers = allUsers.filter(function(u) { return u.role === 'manager' && (u.assignedAgents || []).indexOf(oldName) >= 0; });
                    for (var mi = 0; mi < managers.length; mi++) {
                      var mgr = managers[mi];
                      var updated = (mgr.assignedAgents || []).map(function(a) { return a === oldName ? newName : a; });
                      await updateUser(mgr.uid, { assignedAgents: updated });
                    }
                    if (managers.length > 0) showMsg('Updated ' + managers.length + ' manager(s) with new agent name');
                  }
                  showMsg(ll.saved);
                  setEditUser(null);
                  setUsersLoaded(false);
                }} style={{ flex: 1, padding: 10, borderRadius: 6, border: 'none', background: th.accent, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save</button>
              </div>
            </div>
          )}
        </Modal>

        {/* NEW LEAD NOTIFICATION */}
        {newLeadNotif && (
          <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, animation: 'slideDown 0.5s ease-out' }}>
            <div style={{ background: 'linear-gradient(135deg, #16835e, #2d5a4e)', color: '#fff', borderRadius: 16, padding: '18px 28px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 14, maxWidth: 460, border: '2px solid #4ade80', position: 'relative' }}>
              <div style={{ fontSize: 48, lineHeight: 1, animation: 'bounce 0.6s ease infinite alternate' }}>🎯</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4, color: '#f5d97e' }}>🔥 New Lead Alert!</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{newLeadNotif.client || 'New Client'}</div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>{(newLeadNotif.phone || '') + (newLeadNotif.date ? ' • ' + newLeadNotif.date : '')}</div>
                <div style={{ fontSize: 12, marginTop: 6, color: '#4ade80', fontWeight: 700 }}>Time to shine! Go get them! 💪</div>
              </div>
              <button onClick={function() {
                var clientName = newLeadNotif.client || '';
                var targetId = newLeadNotif._id || '';
                setNewLeadNotif(null);
                setTab('leads');
                setSearch(clientName);
                setHighlightId(targetId);
                setUnseenLeadIds([]);
                setTimeout(function() { setHighlightId(null); }, 300000);
              }}
                style={{ background: '#f5d97e', color: '#1a3c34', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>View Lead →</button>
              <button onClick={function() { setNewLeadNotif(null); }}
                style={{ position: 'absolute', top: 6, right: 10, background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
          </div>
        )}

        {/* CSS Animations */}
        <style>{'\
          @keyframes slideDown { from { opacity: 0; transform: translateX(-50%) translateY(-30px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }\
          @keyframes bounce { from { transform: scale(1); } to { transform: scale(1.15); } }\
          @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.2); } }\
          @keyframes highlightPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); } 50% { box-shadow: 0 0 12px 4px rgba(239,68,68,0.5); } }\
          .highlight-row { animation: highlightPulse 1.5s ease-in-out infinite; background: rgba(239,68,68,0.08) !important; border-left: 4px solid #ef4444 !important; }\
        '}</style>

        {/* REMINDER POPUP */}
        {reminderPopup && (
          <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 99998, animation: 'slideDown 0.5s ease-out' }}>
            <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', borderRadius: 16, padding: '18px 28px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 14, maxWidth: 460, border: '2px solid #fbbf24', position: 'relative' }}>
              <div style={{ fontSize: 48, lineHeight: 1, animation: 'bounce 0.6s ease infinite alternate' }}>🔔</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4, color: '#fff' }}>Reminder!</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{reminderPopup.client || 'Client'}</div>
                <div style={{ fontSize: 11, opacity: 0.9 }}>{reminderPopup.phone || ''}</div>
                {reminderPopup.comments && <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85, fontStyle: 'italic' }}>{reminderPopup.comments.slice(0, 80)}</div>}
              </div>
              <button onClick={function() {
                var clientName = reminderPopup.client || '';
                var targetTab = reminderPopup.type === 'followup' ? 'followup' : 'leads';
                var targetId = reminderPopup.id || '';
                setReminderPopup(null);
                setSearch(clientName);
                setTab(targetTab);
                setHighlightId(targetId);
                setTimeout(function() { setHighlightId(null); }, 300000);
              }}
                style={{ background: '#fff', color: '#d97706', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>{reminderPopup.type === 'followup' ? 'View Client →' : 'View Lead →'}</button>
              <button onClick={function() { setReminderPopup(null); }}
                style={{ position: 'absolute', top: 6, right: 10, background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
          </div>
        )}

        {/* COMMENT POPUP */}
        {commentPopup && (
          <div onClick={function() { setCommentPopup(null); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={function(e) { e.stopPropagation(); }} style={{ background: th.card, borderRadius: 12, padding: '20px 24px', maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid ' + th.border }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: th.text }}>💬 Comment</span>
                <button onClick={function() { setCommentPopup(null); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: th.sub, lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ fontSize: 13, lineHeight: '1.6', wordBreak: 'break-word', whiteSpace: 'pre-wrap', color: th.text, background: '#fffbeb', padding: '12px 14px', borderRadius: 8, border: '1px solid ' + th.border, maxHeight: 300, overflow: 'auto' }}>{commentPopup}</div>
              <button onClick={function() { navigator.clipboard.writeText(commentPopup); showMsg('Copied!'); }} style={{ marginTop: 12, padding: '6px 16px', borderRadius: 6, border: '1px solid ' + th.border, background: th.card, color: th.text, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>📋 Copy</button>
            </div>
          </div>
        )}

        <Toast msg={toast} show={toast !== ''} />

        {/* I.H Signature Footer */}
        {(function() {
          var isLight = th.bg === '#ffffff' || th.bg === '#f5f5f5' || thKey === 'light';
          var lineColor = isLight ? 'rgba(160,120,30,0.5)' : 'rgba(212,175,55,0.35)';
          var txtColor = isLight ? 'rgba(80,60,10,0.7)' : 'rgba(255,255,255,0.3)';
          var sigFilter = isLight ? 'sepia(100%) saturate(500%) brightness(45%) hue-rotate(5deg) contrast(1.2)' : 'invert(85%) sepia(20%) saturate(500%) hue-rotate(5deg) brightness(90%)';
          var sigOpacity = isLight ? 0.9 : 0.7;
          return (
            <div style={{ padding: '16px 0 10px', marginTop: 30 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <div style={{ flex: 1, maxWidth: 80, height: 1, background: 'linear-gradient(to right, transparent, ' + lineColor + ')' }}></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <img src={IH_SIGNATURE} alt="I.H" style={{ height: 22, filter: sigFilter, opacity: sigOpacity }} />
                  <span style={{ fontSize: 9, color: txtColor, letterSpacing: 0.5 }}>Design & Development</span>
                </div>
                <div style={{ flex: 1, maxWidth: 80, height: 1, background: 'linear-gradient(to left, transparent, ' + lineColor + ')' }}></div>
              </div>
            </div>
          );
        })()}
      </div>
    </CopyGuard>
  );
}

// Fix: pieData reference
var pieData = [];

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
